/**
 * Renders the front-elevation view: the to-scale developed elevation, plus a
 * small plan thumbnail with the active wall highlighted.
 *
 * The thumbnail is the orientation aid the owner asked for — in a room with
 * several similar walls it's easy to lose track of which wall an elevation
 * belongs to, so selecting a panel lights up that wall on the mini plan (and
 * vice versa). Geometry comes from the pure layoutElevation()/plan chain; this
 * module only draws and wires taps.
 *
 * Scaling: both axes use one scale factor (units → px) chosen to fit the
 * content, so the drawing is genuinely to scale on screen. Labels are drawn at
 * fixed sizes so they stay readable at any fit.
 *
 * Pan/zoom: the whole drawing sits inside a viewport <g> whose transform the
 * user pans (drag) and zooms (wheel / pinch / the toolbar buttons), exactly
 * like the plan canvas — so a designer can zoom into a panel to work close.
 */
import { carcassExtent, isFlushUnit, layoutElevation, uniformPanelHeight } from '../geometry/index.js';
import { stackHasBaseTrim, stackHasCrownTrim } from '../geometry/trim.js';
import { MAX_SCALE, MIN_SCALE, identityView, panBy, transformString, zoomAt } from './view.js';
import { floatTextInput } from './text-input.js';
import { interiorMarks, shelfGrabBands, shelfDragBounds, clampShelfDrag } from './elevation-interior.js';
// The section caption the Design tab's rows also render (ADR 0066 item 6).
import { sectionCodeLabel } from './section-identity.js';

const SVGNS = 'http://www.w3.org/2000/svg';

// Logical drawing space for the elevation SVG; CSS scales it to the container.
/** Air left around a content-fitted viewBox, so edge strokes are not clipped. */
const VIEWBOX_FIT_PAD = 8;
const ELEV_W = 1200;
const ELEV_H = 560;
const MARGIN = { left: 44, right: 120, top: 34, bottom: 52 };

/**
 * Gap between an obstruction's top edge and its label's baseline, in SCREEN px.
 * Enough to clear the box's stroke without floating away from what it names.
 */
const OBSTRUCTION_LABEL_GAP_PX = 3;
/** Vertical-span tolerance for one related chained dimension run. */
const HORIZONTAL_PLANE_TOLERANCE_UNITS = 0.5;

const THUMB_W = 240;
const THUMB_H = 170;
const THUMB_PAD = 16;

function el(tag, attrs = {}) {
  const node = document.createElementNS(SVGNS, tag);
  for (const [k, v] of Object.entries(attrs)) node.setAttribute(k, v);
  return node;
}

export function createElevation(elevSvg, thumbSvg, handlers = {}) {
  elevSvg.setAttribute('viewBox', `0 0 ${ELEV_W} ${ELEV_H}`);
  elevSvg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
  thumbSvg.setAttribute('viewBox', `0 0 ${THUMB_W} ${THUMB_H}`);
  thumbSvg.setAttribute('preserveAspectRatio', 'xMidYMid meet');

  // Everything is drawn into this viewport group so pan/zoom is one transform.
  const viewport = el('g', { class: 'elev-viewport' });
  elevSvg.append(viewport);
  let view = identityView();

  /**
   * The last frame's unit↔pixel mapping, kept so that pointer positions can be
   * converted back into room units. Drawing is one-way (units → px); dragging
   * needs the inverse, and re-deriving it from the layout on every pointermove
   * would mean re-running the whole elevation solve mid-gesture.
   */
  let frame = null; // { layout, scale, originX, floorY }
  /** id → where a fixture was drawn this frame, so a drag can start from it. */
  const drawnItems = new Map();
  /**
   * elementIndex → {minTopUnits, maxTopUnits} for every shelf drawn draggable
   * this frame. Same idea as `drawnItems`: the gesture answers from the picture
   * on screen, not from a fresh solve taken mid-drag.
   */
  const shelfTravel = new Map();
  /** section id + element index — see the note where this map is filled. */
  const shelfKey = (sectionId, index) => `${sectionId}::${index}`;

  function applyView() {
    viewport.setAttribute('transform', transformString(view));
  }

  /** client coords → the SVG's own coordinate space (independent of the view). */
  function toSvgPoint(clientX, clientY) {
    const pt = elevSvg.createSVGPoint();
    pt.x = clientX;
    pt.y = clientY;
    const ctm = elevSvg.getScreenCTM();
    if (ctm) {
      const m = pt.matrixTransform(ctm.inverse());
      return { x: m.x, y: m.y };
    }
    const box = elevSvg.getBoundingClientRect?.() ?? { left: 0, top: 0 };
    return { x: clientX - box.left, y: clientY - box.top };
  }

  /** client coords → the viewport group's space, i.e. undoing the pan/zoom. */
  function toViewportPoint(clientX, clientY) {
    const p = toSvgPoint(clientX, clientY);
    return { x: (p.x - view.x) / view.scale, y: (p.y - view.y) / view.scale };
  }

  /**
   * client coords → room units: x measured along the developed elevation strip
   * (so it spans every panel end to end), y measured up from the floor.
   */
  function toUnits(clientX, clientY) {
    if (!frame) return null;
    const p = toViewportPoint(clientX, clientY);
    return {
      x: (p.x - frame.originX) / frame.scale,
      y: (frame.floorY - p.y) / frame.scale,
    };
  }

  /** The panel a strip-x lands on, or null past either end / in no man's land. */
  function panelAtStripX(stripX) {
    if (!frame) return null;
    return frame.layout.panels.find((p) => stripX >= p.x && stripX <= p.x + p.width) ?? null;
  }

  /** True when a client point is over the elevation surface at all. */
  function containsPoint(clientX, clientY) {
    const box = elevSvg.getBoundingClientRect?.();
    if (!box) return false;
    return clientX >= box.left && clientX <= box.right && clientY >= box.top && clientY <= box.bottom;
  }

  /**
   * Turns a drop point into a wall-relative placement, treating the point as
   * the CENTRE of the item being dropped — that is where a finger or cursor
   * feels like it is holding something. Returns null if the drop missed every
   * panel; the caller decides whether that means "cancel" or "keep as is".
   */
  function placementAt(clientX, clientY, widthUnits = 0, heightUnits = 0) {
    const u = toUnits(clientX, clientY);
    if (!u) return null;
    const panel = panelAtStripX(u.x);
    if (!panel) return null;
    return {
      wallId: panel.id,
      offsetAlongWall: u.x - panel.x - widthUnits / 2,
      offsetFromFloor: u.y - heightUnits / 2,
    };
  }

  /**
   * Draw the elevation from the current chain + heights + rules.
   *
   * @param {object[]} walls the plan chain (walls + openings)
   * @param {object} opts { pxPerUnit, ceilingHeightUnits, referenceLines, selectedId, formatLength }
   */
  function render(walls, opts) {
    const layout = layoutElevation(walls, {
      pxPerUnit: opts.pxPerUnit,
      ceilingHeightUnits: opts.ceilingHeightUnits,
      referenceLines: opts.referenceLines,
      // Sloped ceilings (ADR 0044): each wall's height profile from the model.
      profileFor: opts.wallHeightProfileFor,
    });
    viewport.replaceChildren();
    drawnItems.clear();
    frame = null;

    if (layout.panels.length === 0) {
      const note = el('text', { x: ELEV_W / 2, y: ELEV_H / 2, 'text-anchor': 'middle', class: 'elev-empty' });
      note.textContent = 'No walls flagged for elevation. Tick “Elev” on a wall, or draw a room.';
      viewport.append(note);
      return;
    }

    // Section width captions live BELOW the floor line now (ADR 0060), so the
    // bottom margin has to carry one extra row before the wall name/length.
    const widthLabelDrop = opts.elevationWidthLabelDropPx > 0 ? opts.elevationWidthLabelDropPx : 13;
    const bottomMargin = MARGIN.bottom + widthLabelDrop;
    const availW = ELEV_W - MARGIN.left - MARGIN.right;
    const availH = ELEV_H - MARGIN.top - bottomMargin;
    const scale = Math.min(availW / layout.totalWidth, availH / layout.totalHeight);

    const originX = MARGIN.left;
    const floorY = ELEV_H - bottomMargin; // floor sits at the bottom margin
    const toX = (units) => originX + units * scale;
    const toY = (unitsFromFloor) => floorY - unitsFromFloor * scale; // flip: up = smaller y
    frame = { layout, scale, originX, floorY };
    shelfTravel.clear();

    const stripRight = toX(layout.totalWidth);

    // To-scale grid behind the elevation (ADR 0024), same 6" cell as the plan.
    if (opts.gridVisible) {
      const tile = (opts.gridSpacingUnits ?? 6) * scale;
      if (tile >= 3) {
        const defs = el('defs');
        const pattern = el('pattern', { id: 'elev-grid', width: tile, height: tile, patternUnits: 'userSpaceOnUse' });
        pattern.append(el('path', { d: `M ${tile} 0 L 0 0 0 ${tile}`, class: 'grid-line' }));
        defs.append(pattern);
        const R = 40000;
        viewport.append(defs, el('rect', { x: -R, y: -R, width: 2 * R, height: 2 * R, fill: 'url(#elev-grid)', class: 'grid-rect' }));
      }
    }

    // Reference lines span the whole strip, labelled at the right. Only the
    // ones passed in are drawn — the caller filters by the visibility toggles.
    for (const line of layout.referenceLines) {
      if (line.y > layout.totalHeight + 0.01) continue;
      const y = toY(line.y);
      viewport.append(el('line', {
        x1: originX, y1: y, x2: stripRight, y2: y,
        class: `elev-ref elev-ref--${line.style}`,
      }));
      const label = el('text', { x: stripRight + 8, y: y + 4, class: 'elev-ref__label' });
      label.textContent = line.label;
      viewport.append(label);
    }

    // Floor line.
    viewport.append(el('line', { x1: originX, y1: floorY, x2: stripRight, y2: floorY, class: 'elev-floor' }));

    /**
     * A wall-height dimension: floor to wall top, ticked at both ends, with the
     * figure read bottom-up beside it the way a drafting dimension is.
     */
    const heightDimension = (atX, heightUnits) => {
      const topY = toY(heightUnits);
      const tick = 5;
      const g = el('g', { class: 'elev-height-dim' });
      g.append(el('line', { x1: atX, y1: floorY, x2: atX, y2: topY }));
      g.append(el('line', { x1: atX - tick, y1: floorY, x2: atX + tick, y2: floorY }));
      g.append(el('line', { x1: atX - tick, y1: topY, x2: atX + tick, y2: topY }));
      const midY = (floorY + topY) / 2;
      const label = el('text', {
        x: atX - 6, y: midY,
        transform: `rotate(-90 ${atX - 6} ${midY})`,
        'text-anchor': 'middle',
        class: 'elev-height-dim__label',
      });
      label.textContent = opts.formatLength ? opts.formatLength(heightUnits) : `${Math.round(heightUnits)}"`;
      g.append(label);
      viewport.append(g);
    };

    // Wall height (owner, 2026-07-29). One dimension for the whole elevation
    // when every wall shares one flat height — the usual room — and otherwise
    // one per wall: at BOTH ends of a sloped wall, since neither figure alone
    // describes it, and once for a wall that is flat at its own height.
    const commonHeight = uniformPanelHeight(layout.panels);
    const flat = (panel) => {
      const pts = panel.profile ?? [];
      return pts.length < 2 || pts.every((pt) => Math.abs(pt.heightUnits - pts[0].heightUnits) <= 0.01);
    };
    if (commonHeight !== null && layout.panels.every(flat)) {
      heightDimension(originX - 14, commonHeight);
    } else {
      for (const panel of layout.panels) {
        const pts = panel.profile ?? [];
        if (flat(panel)) {
          heightDimension(toX(panel.x) + 14, panel.height);
          continue;
        }
        heightDimension(toX(panel.x) + 14, pts[0].heightUnits);
        heightDimension(toX(panel.x + panel.width) - 14, pts[pts.length - 1].heightUnits);
      }
    }

    /** A horizontal field dimension with inward-facing arrowheads and a centred label. */
    const horizontalMeasurement = (parent, x1, x2, y, labelText) => {
      const span = x2 - x1;
      // Fixed-size sheet text needs real breathing room. If the value cannot
      // fit between its arrowheads (a common case for outlets only 1" apart),
      // omit that ONE elevation span; the measurement table still carries the
      // exact values. Estimating is intentional — SVG text has no reliable
      // getBBox in the off-screen/print renderer.
      const minimumReadableSpan = Math.max(24, String(labelText).length * 8 + 12);
      if (!(span >= minimumReadableSpan)) return;
      const arrow = Math.min(5, Math.max(2.5, (x2 - x1) / 5));
      const wing = 3;
      const g = el('g', { class: 'elev-measurement-dim' });
      g.append(
        el('line', { x1, y1: y, x2, y2: y, class: 'elev-measurement-dim__line' }),
        el('path', { d: `M ${x1 + arrow} ${y - wing} L ${x1} ${y} L ${x1 + arrow} ${y + wing}`, class: 'elev-measurement-dim__arrow' }),
        el('path', { d: `M ${x2 - arrow} ${y - wing} L ${x2} ${y} L ${x2 - arrow} ${y + wing}`, class: 'elev-measurement-dim__arrow' }),
      );
      const label = el('text', {
        x: (x1 + x2) / 2,
        y: y - 5,
        'text-anchor': 'middle',
        class: 'elev-measurement-dim__label',
      });
      label.textContent = labelText;
      g.append(label);
      parent.append(g);
    };

    /** A floor-to-obstruction dimension, with the value reading up the line. */
    const verticalFloorMeasurement = (parent, x, topY, bottomY, labelText, leaderToX = null) => {
      const span = bottomY - topY;
      // Unlike a horizontal clear-span, FF is a required measurement for every
      // obstruction. Never suppress it merely because a fraction is long or
      // the fixture is close to the floor; collision handling below chooses a
      // separate lane when the natural centre line is occupied.
      if (!(span > 0.5)) {
        const zeroLabel = el('text', {
          x,
          y: bottomY - 5,
          'text-anchor': 'middle',
          class: 'elev-floor-dim__label elev-floor-dim__label--zero',
        });
        zeroLabel.textContent = labelText;
        parent.append(zeroLabel);
        return;
      }
      const arrow = Math.min(5, Math.max(2.5, span / 5));
      const wing = 3;
      parent.append(
        el('line', { x1: x, y1: topY, x2: x, y2: bottomY, class: 'elev-floor-dim__line' }),
        el('path', { d: `M ${x - wing} ${topY + arrow} L ${x} ${topY} L ${x + wing} ${topY + arrow}`, class: 'elev-floor-dim__arrow' }),
        el('path', { d: `M ${x - wing} ${bottomY - arrow} L ${x} ${bottomY} L ${x + wing} ${bottomY - arrow}`, class: 'elev-floor-dim__arrow' }),
      );
      if (leaderToX !== null && Math.abs(leaderToX - x) > 0.5) {
        parent.append(el('line', {
          x1: x, y1: topY, x2: leaderToX, y2: topY, class: 'elev-floor-dim__leader',
        }));
      }
      const midY = (topY + bottomY) / 2;
      const label = el('text', {
        x: x - 5,
        y: midY,
        transform: `rotate(-90 ${x - 5} ${midY})`,
        'text-anchor': 'middle',
        class: 'elev-floor-dim__label',
      });
      label.textContent = labelText;
      parent.append(label);
    };

    /** A ceiling-to-feature-bottom dimension. Short drops use a side label. */
    const verticalCeilingMeasurement = (parent, x, ceilingY, bottomY, labelText) => {
      const span = bottomY - ceilingY;
      if (!(span > 0.5)) return;
      const arrow = Math.min(5, Math.max(2, span / 4));
      const wing = 3;
      parent.append(
        el('line', { x1: x, y1: ceilingY, x2: x, y2: bottomY, class: 'elev-ceiling-drop-dim__line' }),
        el('path', { d: `M ${x - wing} ${ceilingY + arrow} L ${x} ${ceilingY} L ${x + wing} ${ceilingY + arrow}`, class: 'elev-ceiling-drop-dim__arrow' }),
        el('path', { d: `M ${x - wing} ${bottomY - arrow} L ${x} ${bottomY} L ${x + wing} ${bottomY - arrow}`, class: 'elev-ceiling-drop-dim__arrow' }),
      );
      const label = el('text', {
        x: x + 6,
        y: span >= 20 ? (ceilingY + bottomY) / 2 : bottomY + 12,
        class: `elev-ceiling-drop-dim__label${span < 20 ? ' elev-ceiling-drop-dim__label--compact' : ''}`,
      });
      label.textContent = labelText;
      parent.append(label);
    };

    /** A compact FF value directly below an upper obstruction when a clear gap exists. */
    const belowObstructionFloorMeasurement = (parent, x, itemBottomY, blockerTopY, labelText) => {
      const label = el('text', {
        x,
        y: (itemBottomY + blockerTopY) / 2 + 4,
        'text-anchor': 'middle',
        class: 'elev-floor-dim__label elev-floor-dim__label--below',
      });
      label.textContent = labelText;
      parent.append(label);
    };

    // Wall panels.
    for (const panel of layout.panels) {
      const x = toX(panel.x);
      const w = panel.width * scale;
      const top = toY(panel.height);
      const h = panel.height * scale;
      const selected = panel.id === opts.selectedId;

      // The wall's TOP follows its height profile (ADR 0044): flat walls give a
      // rectangle, a sloped one a polygon whose top edge runs corner to corner
      // through any interior breakpoints. Drawn as a polygon either way so there
      // is one code path — a flat profile is simply the degenerate case.
      const topPoints = (panel.profile ?? [
        { atUnits: 0, heightUnits: panel.height },
        { atUnits: panel.width, heightUnits: panel.height },
      ]).map((pt) => `${toX(panel.x + pt.atUnits).toFixed(1)},${toY(pt.heightUnits).toFixed(1)}`);
      const shape = el('polygon', {
        points: [`${x.toFixed(1)},${floorY.toFixed(1)}`, ...topPoints, `${(x + w).toFixed(1)},${floorY.toFixed(1)}`].join(' '),
        class: `elev-panel${selected ? ' elev-panel--selected' : ''}`,
        'data-id': panel.id,
      });
      viewport.append(shape);
      void top;
      void h;

      // Rectangular ceiling features hang down from the ceiling. A fan's
      // projection instead sits at its measured finished-floor height; that is
      // the production clearance Engineering needs, not a guessed ceiling drop.
      const panelCeilingOpenings = opts.ceilingOpeningsFor?.(panel.id) ?? [];
      for (const opening of panelCeilingOpenings) {
        const fan = opening.kind === 'ceiling_fan' && Number.isFinite(opening.fromFloorUnits);
        const featureBottomUnits = fan ? opening.fromFloorUnits : panel.height - opening.heightUnits;
        const featureTopUnits = fan ? opening.fromFloorUnits + Math.max(opening.heightUnits, 1) : panel.height;
        viewport.append(el('rect', {
          x: toX(panel.x + opening.fromUnits),
          y: toY(featureTopUnits),
          width: Math.max((opening.toUnits - opening.fromUnits) * scale, 1),
          height: Math.max((featureTopUnits - featureBottomUnits) * scale, 1),
          class: 'elev-ceiling-opening',
          'data-ceiling-feature-kind': fan ? 'ceiling_fan' : (opening.kind ?? ''),
          'data-ceiling-opening-id': opening.openingId ?? '',
        }));
      }

      // Measurement-sheet only: ceiling features get the same wall-edge
      // clearances that ordinary wall obstructions do, plus the applicable
      // vertical dimension (ceiling drop or fan FF). Interactive elevations omit
      // these because the callback is
      // supplied only by measurement-sheet.js.
      const measuredCeilingOpenings = panelCeilingOpenings
        .map((opening) => ({ opening, measurement: opts.measurementForCeilingOpening?.(opening.openingId, panel.id) }))
        .filter((entry) => entry.measurement)
        .sort((a, b) => a.opening.fromUnits - b.opening.fromUnits);
      const measuredFans = measuredCeilingOpenings.filter(({ opening }) => opening.kind === 'ceiling_fan' && Number.isFinite(opening.fromFloorUnits));
      const measuredCeilingDrops = measuredCeilingOpenings.filter(({ opening }) => opening.kind !== 'ceiling_fan' || !Number.isFinite(opening.fromFloorUnits));
      const ceilingFormatMeasurement = opts.formatLength ?? ((units) => `${Math.round(units)}"`);
      if (measuredCeilingDrops.length) {
        const occupiedRuns = [];
        for (const { opening } of measuredCeilingDrops) {
          const startUnits = Math.max(0, opening.fromUnits);
          const endUnits = Math.min(panel.width, opening.toUnits);
          const run = occupiedRuns.at(-1);
          if (run && startUnits <= run.endUnits + 0.01) run.endUnits = Math.max(run.endUnits, endUnits);
          else occupiedRuns.push({ startUnits, endUnits });
        }
        const maxDropPx = Math.max(...measuredCeilingDrops.map(({ opening }) => opening.heightUnits * scale), 1);
        const dimensionY = toY(panel.height) + maxDropPx + 30;
        const dimensions = el('g', {
          class: 'elev-ceiling-opening-dimensions',
          'data-wall-id': panel.id,
        });
        const first = occupiedRuns[0];
        const last = occupiedRuns.at(-1);
        horizontalMeasurement(dimensions, x, toX(panel.x + first.startUnits), dimensionY, ceilingFormatMeasurement(first.startUnits));
        for (let index = 1; index < occupiedRuns.length; index += 1) {
          const previous = occupiedRuns[index - 1];
          const currentRun = occupiedRuns[index];
          horizontalMeasurement(
            dimensions,
            toX(panel.x + previous.endUnits),
            toX(panel.x + currentRun.startUnits),
            dimensionY,
            ceilingFormatMeasurement(currentRun.startUnits - previous.endUnits),
          );
        }
        horizontalMeasurement(
          dimensions,
          toX(panel.x + last.endUnits),
          x + w,
          dimensionY,
          ceilingFormatMeasurement(Math.max(0, panel.width - last.endUnits)),
        );
        viewport.append(dimensions);

        measuredCeilingDrops.forEach(({ opening }, index) => {
          const drop = el('g', {
            class: 'elev-ceiling-drop-dim',
            'data-ceiling-opening-id': opening.openingId ?? '',
          });
          const ceilingY = toY(panel.height);
          verticalCeilingMeasurement(
            drop,
            toX(panel.x + opening.toUnits) + 8 + index * 12,
            ceilingY,
            ceilingY + Math.max(opening.heightUnits * scale, 1),
            ceilingFormatMeasurement(opening.heightUnits),
          );
          viewport.append(drop);
        });
      }
      measuredFans.forEach(({ opening }, index) => {
        const dimensions = el('g', {
          class: 'elev-ceiling-opening-dimensions elev-ceiling-fan-dimensions',
          'data-ceiling-opening-id': opening.openingId ?? '',
        });
        const dimensionY = toY(opening.fromFloorUnits) + 25 + index * 18;
        horizontalMeasurement(
          dimensions,
          x,
          toX(panel.x + opening.fromUnits),
          dimensionY,
          ceilingFormatMeasurement(Math.max(0, opening.fromUnits)),
        );
        horizontalMeasurement(
          dimensions,
          toX(panel.x + opening.toUnits),
          x + w,
          dimensionY,
          ceilingFormatMeasurement(Math.max(0, panel.width - opening.toUnits)),
        );
        const floor = el('g', {
          class: 'elev-ceiling-feature-floor-dim',
          'data-ceiling-opening-id': opening.openingId ?? '',
        });
        verticalFloorMeasurement(
          floor,
          toX(panel.x + opening.toUnits) + 10 + index * 12,
          toY(opening.fromFloorUnits),
          floorY,
          ceilingFormatMeasurement(opening.fromFloorUnits),
        );
        viewport.append(dimensions, floor);
      });

      // Drop-down attic stairs are ceiling-hosted but occupy real floor space
      // when extended. A wall parallel to their width sees two vertical edges;
      // a wall parallel to their run sees the ceiling-to-floor diagonal.
      for (const stair of opts.atticStairsFor?.(panel.id) ?? []) {
        const group = el('g', {
          class: `elev-attic-stair elev-attic-stair--${stair.kind}`,
          'data-ceiling-opening-id': stair.openingId ?? '',
        });
        if (stair.kind === 'front') {
          const left = toX(panel.x + stair.fromUnits);
          const right = toX(panel.x + stair.toUnits);
          const ceilingY = toY(panel.height);
          group.append(
            el('line', { x1: left, y1: ceilingY, x2: left, y2: floorY }),
            el('line', { x1: right, y1: ceilingY, x2: right, y2: floorY }),
            el('line', { x1: left, y1: ceilingY, x2: right, y2: ceilingY }),
          );
        } else if (stair.kind === 'side') {
          group.append(el('line', {
            x1: toX(panel.x + stair.ceilingAtUnits), y1: toY(panel.height),
            x2: toX(panel.x + stair.floorAtUnits), y2: floorY,
          }));
        }
        viewport.append(group);
      }

      // Staged bucket cards ASSIGNED to this wall (ADR 0059): a generic
      // dashed glyph + count near the top of the panel — spatial context for
      // a card that is still a bucket. Deliberately schematic: assignment is
      // not placement, so nothing here pretends to be a drawn section.
      const staged = opts.stagedForWall?.(panel.id) ?? [];
      staged.forEach((s2, i2) => {
        const gy = toY(panel.height) + 14 + i2 * 22;
        const g2 = el('g', { class: 'elev-staged' });
        g2.append(el('rect', { x: x + 8, y: gy, width: 14, height: 14, class: 'elev-staged__box' }));
        const t2 = el('text', { x: x + 27, y: gy + 11, class: 'elev-staged__label' });
        t2.textContent = `${s2.count} × ${s2.label} (staged)`;
        g2.append(t2);
        viewport.append(g2);
      });

      // The end panel of the run on the wall AROUND THE CORNER, standing in
      // front of this one (owner, 2026-08-05). Drawn BEFORE the fixtures, so an
      // outlet it would block still reads on top of it rather than under it —
      // the point is to show the two overlapping, not to hide one.
      for (const ab of opts.abuttingPanelsFor?.(panel.id) ?? []) {
        const ax = toX(panel.x + ab.fromUnits);
        const aw = (ab.toUnits - ab.fromUnits) * scale;
        const ay = toY(ab.topUnits);
        const ah = (ab.topUnits - ab.bottomUnits) * scale;
        if (!(aw > 0) || !(ah > 0)) continue;
        viewport.append(el('rect', {
          x: ax, y: ay, width: aw, height: ah,
          class: 'elev-abutting-panel',
        }));
        // RS-4b — the clear space a HANGING abutting section needs, drawn as a
        // second band beyond the panel rather than by widening it. The owner:
        // "the wall shows the depth of the panel (e.g. 12\") and not the
        // additional hanging clearance space (i.e. 24\" less the depth of the
        // panel). This is already shown in the plan view and I'd like it to be
        // represented in the elevation view."
        //
        // NOT `elev-hang-clearance`, despite the name fitting: that class is
        // ADR 0038's ALONG-WALL corner reservation on this wall's own run, a
        // different thing that happens to share a word.
        if (Number.isFinite(ab.clearanceFromUnits) && Number.isFinite(ab.clearanceToUnits)) {
          const cw = (ab.clearanceToUnits - ab.clearanceFromUnits) * scale;
          if (cw > 0) {
            viewport.append(el('rect', {
              x: toX(panel.x + ab.clearanceFromUnits), y: ay, width: cw, height: ah,
              class: 'elev-abutting-clearance',
            }));
            const cap = el('text', {
              x: toX(panel.x + (ab.clearanceFromUnits + ab.clearanceToUnits) / 2),
              y: ay + ah * 0.5,
              'text-anchor': 'middle',
              class: 'elev-abutting-clearance__label',
            });
            cap.textContent = opts.formatLength ? opts.formatLength(ab.clearanceUnits) : `${ab.clearanceUnits}`;
            viewport.append(cap);
          }
        }
      }

      // Items mounted on this wall, positioned wall-relative: offsetAlongWall
      // from the wall's start and offsetFromFloor to the item's bottom edge.
      const panelObstructions = opts.obstructionsFor?.(panel.id) ?? [];
      for (const item of panelObstructions) {
        const stripX = panel.x + item.offsetAlongWall;
        const ix = toX(stripX);
        const iw = item.widthUnits * scale;
        const ih = item.heightUnits * scale;
        const iy = toY(item.offsetFromFloor + item.heightUnits);

        // Each fixture is a single <g> so a drag translates it as a unit —
        // label included.
        const group = el('g', {
          class: `elev-obstruction elev-obstruction--${item.type}${item.locked ? ' elev-obstruction--locked' : ''}`,
          'data-obstruction-id': item.id,
        });
        group.append(el('rect', { x: ix, y: iy, width: iw, height: ih, class: 'elev-obstruction__box' }));

        // A cased opening is two physical silhouettes: the overall casing and
        // the usable opening inside it. Draw both only when the inner size was
        // actually recorded; never invent a trim width. Doors currently record
        // clear width but not clear height, so their inner opening stays
        // floor-to-top and shows the measured side casing. Window records may
        // also supply both clear dimensions through the same opening metadata.
        const clearWidthUnits = item.opening?.trim !== 'uncased'
          && Number.isFinite(item.opening?.clearOpeningWidthUnits)
          ? item.opening.clearOpeningWidthUnits : null;
        const clearHeightUnits = Number.isFinite(item.opening?.clearOpeningHeightUnits)
          ? item.opening.clearOpeningHeightUnits
          : item.type === 'wall_opening' ? item.heightUnits : null;
        const hasRecordedClearOpening = clearWidthUnits > 0
          && clearWidthUnits <= item.widthUnits
          && clearHeightUnits > 0
          && clearHeightUnits <= item.heightUnits
          && (clearWidthUnits < item.widthUnits - 0.01 || clearHeightUnits < item.heightUnits - 0.01);
        if (hasRecordedClearOpening) {
          const sideInsetUnits = (item.widthUnits - clearWidthUnits) / 2;
          const bottomInsetUnits = item.type === 'wall_opening'
            ? 0 : (item.heightUnits - clearHeightUnits) / 2;
          group.append(el('rect', {
            x: ix + sideInsetUnits * scale,
            y: toY(item.offsetFromFloor + bottomInsetUnits + clearHeightUnits),
            width: clearWidthUnits * scale,
            height: clearHeightUnits * scale,
            class: 'elev-obstruction__clear-opening',
            'data-clear-width-units': clearWidthUnits,
            'data-clear-height-units': clearHeightUnits,
          }));
        }

        // Name the fixture ABOVE its box (owner, 2026-08-05 — the label was
        // missing and is wanted back).
        //
        // It was removed on 2026-07-24 for a specific reason: it used to sit
        // INSIDE the box, and a label big enough to read swamped the outlet it
        // named. Putting it above solves that outright — the text can never
        // cover the shape however small the shape is — so the fixture stays
        // readable AND identified, which is what the elevation is for.
        //
        // Sized in screen px like the padlock, so it neither shrinks to nothing
        // nor swells to fill the wall as the designer zooms.
        // Just the CODE — O1, W2, S1 (owner, 2026-08-05). A drawing has room
        // for a key, not a description: beside a 3"-wide outlet even the word
        // "Outlet" is wider than the thing it names. The wall number,
        // description and measurements are in the measurement sheet's per-wall
        // table, which has columns for them.
        const name = opts.obstructionCodeFor?.(item.id) || '';
        if (name) {
          const tag = el('text', {
            x: ix + iw / 2,
            y: iy - OBSTRUCTION_LABEL_GAP_PX,
            'text-anchor': 'middle',
            class: 'elev-obstruction__label',
          });
          tag.textContent = name;
          group.append(tag);
        }
        // A ceiling-capping item (a soffit) states its UNDERSIDE from-floor
        // height right on the design elevation (owner, 2026-08-29: "Soffit
        // should show From Floor FF height to make setting target height
        // easier") — the underside is what a stack must clear, and the
        // measured-sheet FF machinery only labels measured items. Membership
        // by the type's capsSectionHeight property, never the key (D110);
        // the value is the item's live offsetFromFloor, so a dragged or
        // edited soffit moves its own number. Style matches the existing
        // floor-dim labels on this view.
        if (opts.capsSectionHeightTypeKeys?.has?.(item.type) && item.offsetFromFloor > 0) {
          const ff = el('text', {
            x: ix + iw / 2,
            y: toY(item.offsetFromFloor) + 14,
            'text-anchor': 'middle',
            class: 'elev-floor-dim__label elev-floor-dim__label--soffit',
          });
          ff.textContent = `FF ${(opts.formatLength ?? ((u) => `${Math.round(u)}"`))(item.offsetFromFloor)}`;
          group.append(ff);
        }
        // A small padlock glyph on locked fixtures, sized in screen px so it
        // stays legible at any zoom, centered on the box.
        if (item.locked) {
          const lock = el('text', {
            x: ix + iw / 2, y: iy + ih / 2 + 4,
            'text-anchor': 'middle', class: 'elev-obstruction__lock',
          });
          lock.textContent = '🔒';
          group.append(lock);
        }
        viewport.append(group);
        drawnItems.set(item.id, {
          stripX,
          offsetFromFloor: item.offsetFromFloor,
          widthUnits: item.widthUnits,
          heightUnits: item.heightUnits,
          wallId: panel.id,
          locked: item.locked === true,
        });
      }

      // Measurement-sheet only: obstructions whose VERTICAL spans overlap form
      // ONE chained run. That makes each clear span appear exactly once:
      // wall → first item → neighbouring items → wall. The chain rides at the
      // vertical midpoint of the entire related group, so fixtures with
      // different bottoms/heights still share a legible common dimension line.
      const measuredItems = panelObstructions
        .map((item) => ({ item, measurement: opts.measurementForObstruction?.(item.id) }))
        .filter((entry) => entry.measurement)
        .sort((a, b) => a.item.offsetFromFloor - b.item.offsetFromFloor
          || a.item.offsetAlongWall - b.item.offsetAlongWall);
      const measurementPlanes = [];
      for (const entry of measuredItems) {
        const bottomUnits = entry.item.offsetFromFloor;
        const topUnits = bottomUnits + entry.item.heightUnits;
        // Intervals arrive bottom-first. If this one begins before the current
        // group's highest top, a horizontal line exists that crosses both; the
        // group remains connected even when a later item bridges two heights.
        const plane = measurementPlanes.at(-1);
        if (plane && bottomUnits <= plane.topUnits + HORIZONTAL_PLANE_TOLERANCE_UNITS) {
          plane.items.push(entry);
          plane.bottomUnits = Math.min(plane.bottomUnits, bottomUnits);
          plane.topUnits = Math.max(plane.topUnits, topUnits);
        } else {
          measurementPlanes.push({ bottomUnits, topUnits, items: [entry] });
        }
      }
      const formatMeasurement = opts.formatLength ?? ((units) => `${Math.round(units)}"`);
      for (const plane of measurementPlanes) {
        plane.items.sort((a, b) => a.item.offsetAlongWall - b.item.offsetAlongWall);
        // Collapse horizontally overlapping silhouettes before measuring the
        // clear spans. Otherwise a small fixture sitting inside a wide window's
        // X range could create a false gap from its own edge even though the
        // wider obstruction still occupies that space.
        const occupiedRuns = [];
        for (const { item } of plane.items) {
          const startUnits = item.offsetAlongWall;
          const endUnits = startUnits + item.widthUnits;
          const run = occupiedRuns.at(-1);
          if (run && startUnits <= run.endUnits + 0.01) run.endUnits = Math.max(run.endUnits, endUnits);
          else occupiedRuns.push({ startUnits, endUnits });
        }
        const first = occupiedRuns[0];
        const last = occupiedRuns[occupiedRuns.length - 1];
        const dimensionHeightUnits = (plane.bottomUnits + plane.topUnits) / 2;
        const dimensionY = toY(dimensionHeightUnits);
        const dimensions = el('g', {
          class: 'elev-obstruction-dimensions',
          'data-horizontal-plane': String(dimensionHeightUnits),
        });
        horizontalMeasurement(
          dimensions,
          x,
          toX(panel.x + first.startUnits),
          dimensionY,
          formatMeasurement(first.startUnits),
        );
        for (let index = 1; index < occupiedRuns.length; index += 1) {
          const previous = occupiedRuns[index - 1];
          const currentRun = occupiedRuns[index];
          const gapUnits = currentRun.startUnits - previous.endUnits;
          horizontalMeasurement(
            dimensions,
            toX(panel.x + previous.endUnits),
            toX(panel.x + currentRun.startUnits),
            dimensionY,
            formatMeasurement(gapUnits),
          );
        }
        horizontalMeasurement(
          dimensions,
          toX(panel.x + last.endUnits),
          x + w,
          dimensionY,
          formatMeasurement(Math.max(0, panel.width - last.endUnits)),
        );
        viewport.append(dimensions);
      }

      // FF is mandatory for EVERY item, even when values repeat. Prefer the
      // natural centre line; exact overlaps get separate side lanes. When an
      // upper fixture is directly above a lower one, a sufficiently generous
      // clear gap carries the upper value directly below that fixture. Tight
      // stacks use a side lane instead, so no FF value silently disappears.
      const usedFloorLanes = [];
      const usedBelowLabels = [];
      for (const { item } of measuredItems) {
        const itemLeftX = toX(panel.x + item.offsetAlongWall);
        const itemRightX = toX(panel.x + item.offsetAlongWall + item.widthUnits);
        const centerX = (itemLeftX + itemRightX) / 2;
        const itemBottomY = toY(item.offsetFromFloor);
        const labelText = formatMeasurement(item.offsetFromFloor);
        const lowerBlockers = measuredItems
          .map((entry) => entry.item)
          .filter((other) => other.id !== item.id
            && other.offsetAlongWall < item.offsetAlongWall + item.widthUnits - 0.01
            && other.offsetAlongWall + other.widthUnits > item.offsetAlongWall + 0.01
            && other.offsetFromFloor + other.heightUnits <= item.offsetFromFloor + 0.01);
        const highestBlockerTop = lowerBlockers.reduce(
          (highest, other) => Math.max(highest, other.offsetFromFloor + other.heightUnits),
          -Infinity,
        );
        const blockerTopY = Number.isFinite(highestBlockerTop) ? toY(highestBlockerTop) : null;
        const clearGapPx = blockerTopY === null ? 0 : blockerTopY - itemBottomY;
        const estimatedLabelWidth = Math.max(18, String(labelText).length * 8);
        const belowBox = {
          left: centerX - estimatedLabelWidth / 2,
          right: centerX + estimatedLabelWidth / 2,
          top: itemBottomY,
          bottom: blockerTopY,
        };
        const belowCollides = usedBelowLabels.some((box) => belowBox.left < box.right + 4
          && belowBox.right > box.left - 4
          && belowBox.top < box.bottom + 4
          && belowBox.bottom > box.top - 4);

        const floorDimension = el('g', {
          class: 'elev-floor-dim',
          'data-obstruction-id': item.id,
        });
        if (clearGapPx >= 20 && !belowCollides) {
          floorDimension.setAttribute('class', 'elev-floor-dim elev-floor-dim--below');
          floorDimension.setAttribute('data-placement', 'below');
          belowObstructionFloorMeasurement(
            floorDimension, centerX, itemBottomY, blockerTopY, labelText,
          );
          usedBelowLabels.push(belowBox);
          viewport.append(floorDimension);
          continue;
        }

        const laneCandidates = [
          { x: centerX, leaderToX: null },
          { x: itemLeftX - 10, leaderToX: itemLeftX },
          { x: itemRightX + 10, leaderToX: itemRightX },
          { x: itemLeftX - 22, leaderToX: itemLeftX },
          { x: itemRightX + 22, leaderToX: itemRightX },
          { x: itemLeftX - 34, leaderToX: itemLeftX },
          { x: itemRightX + 34, leaderToX: itemRightX },
        ];
        const lineCrossesLowerFixture = (laneX) => measuredItems.some(({ item: other }) => {
          if (other.id === item.id
            || other.offsetFromFloor + other.heightUnits > item.offsetFromFloor + 0.01) return false;
          const otherLeftX = toX(panel.x + other.offsetAlongWall);
          const otherRightX = toX(panel.x + other.offsetAlongWall + other.widthUnits);
          return laneX > otherLeftX - 2 && laneX < otherRightX + 2;
        });
        const laneIsUsed = (laneX) => usedFloorLanes.some((lane) => Math.abs(lane.x - laneX) < 12);
        const lane = laneCandidates.find((candidate) => !laneIsUsed(candidate.x)
          && !lineCrossesLowerFixture(candidate.x))
          ?? { x: itemRightX + 46 + usedFloorLanes.length * 12, leaderToX: itemRightX };
        const isSideLane = Math.abs(lane.x - centerX) > 0.5;
        if (isSideLane) {
          floorDimension.setAttribute('class', 'elev-floor-dim elev-floor-dim--side');
          floorDimension.setAttribute('data-placement', 'side');
        } else {
          floorDimension.setAttribute('data-placement', 'center');
        }
        verticalFloorMeasurement(
          floorDimension, lane.x, itemBottomY, floorY, labelText, lane.leaderToX,
        );
        usedFloorLanes.push({ x: lane.x });
        viewport.append(floorDimension);
      }

      // Wall sections (ADR 0037): the solved run for this wall — vertical
      // divider panels floor→height, each opening captioned with its type code
      // and SOLVED opening width. Drawn from the same layout the plan and the
      // side panel use, so all three always agree. Non-interactive (pointer
      // events pass through to the wall panel) — sections are edited in the
      // side panel, their geometry is computed, not dragged.
      // L-shelf legs reaching along THIS wall from a corner shelf whose body
      // lives in the neighbouring wall's run (ADR 0042). Fetched before the
      // section run because the run's reserved end is the space they occupy —
      // the hatched "reserved" band must not be drawn under a leg that fills it.
      const lLegs = opts.lShelfLegsFor?.(panel.id) ?? [];
      /** How much of this wall's reserved end a drawn L-shelf leg already fills. */
      const legCoverage = (atStart) => lLegs.reduce((max, l) => {
        if (!l.place || !(l.place.topUnits > l.place.bottomUnits)) return max;
        const covers = atStart ? l.fromUnits <= 0.01 : l.toUnits >= panel.width - 0.01;
        return covers ? Math.max(max, l.toUnits - l.fromUnits) : max;
      }, 0);

      const secLayout = opts.sectionLayoutFor?.(panel.id);
      if (secLayout) {
        const secs = opts.sectionsFor?.(panel.id) ?? [];
        const defH = opts.sectionDefaultHeightUnits > 0 ? opts.sectionDefaultHeightUnits : 84;
        // Open sections contribute no height: a divider beside one only rises
        // as tall as its REAL neighbour.
        const secH = (i) => {
          if (i < 0 || i >= secs.length || secLayout.items[i]?.open) return 0;
          return secs[i]?.heightUnits > 0 ? secs[i].heightUnits : defH;
        };
        // Overflow (locked widths don't fit) flags the WHOLE run red on the
        // drawing, matching the plan (owner, 2026-07-27).
        const runOverflows = secLayout.overflowUnits > 0.01;

        // Vertical placement comes from the MODEL (ADR 0040 platforms + ADR 0041
        // stacking), so the drawing can never disagree with the panel or the
        // storage summary. `stacks[i].units` is the run section plus everything
        // stacked on it, bottom → top, each already placed floor-up.
        const platformUnits = opts.platformUnits > 0 ? opts.platformUnits : 0;
        /**
         * `SV-16"` — the code with its DEPTH (owner, 2026-08-04). The label
         * itself now lives in section-identity.js because the Design tab's
         * section rows render the SAME string (ADR 0066 item 6): a row and the
         * shape it draws must never caption themselves differently.
         */
        const codeWithDepth = (typeCode, depthUnits) => sectionCodeLabel(typeCode, depthUnits, opts.formatLength);
        const stacks = opts.stacksFor?.(panel.id) ?? [];
        const unitsOf = (i) => {
          if (i < 0 || i >= secs.length || secLayout.items[i]?.open) return [];
          return stacks[i]?.units ?? [];
        };

        // A connecting span is a configurable section in its own right. Its
        // width remains reserved even when both boards are off; in that case
        // the elevation intentionally shows only the open gap. Otherwise the
        // boards align to the adjoining section's solved top/bottom shelves.
        for (const shelf of opts.connectingShelvesFor?.(panel.id) ?? []) {
          const index = secs.findIndex((section) => section.id === shelf.sectionId);
          const units = unitsOf(index);
          if (!units.length) continue;
          const x = toX(panel.x + shelf.fromUnits);
          const width = Math.max((shelf.toUnits - shelf.fromUnits) * scale, 1);
          const base = units[0];
          const top = units[units.length - 1];
          const drawBoard = (placement) => {
            if (!placement) return;
            viewport.append(el('rect', {
              x,
              y: toY(placement.topUnits),
              width,
              height: Math.max((placement.topUnits - placement.bottomUnits) * scale, 1),
              class: 'elev-connecting-shelf',
            }));
          };
          if (shelf.bottomShelf !== false) drawBoard(base.shelves?.bottom);
          if (shelf.topShelf !== false) drawBoard(top.shelves?.cap ?? top.shelves?.top);
        }

        // Selected crown/base trim is visible, not just priced. Crown rises
        // from the top of each related stack; base starts on the floor and
        // overlaps the lower third of the 3/4" bottom shelf by default.
        //
        // CONTINUOUS strips (owner, 2026-08-28): trim sits IN FRONT of the
        // carcass, so it never breaks at a divider — consecutive sections
        // share one strip that also covers the panels between and beside
        // them. The strips break only where the physical trim breaks: at an
        // OPEN section (nothing to run over), where the CROWN steps at a
        // height change (it wraps the taller unit, as pricing always said),
        // and where BASE stops at a section whose bottom shelf is off.
        //
        // `secLayout.panels` is a compact list of panels that physically
        // exist, not an item-indexed boundary list: OPEN ends and OPEN→OPEN
        // joins are omitted. Associate panels and sections by their solved
        // positions, just as the plan renderer does (ADR 0037).
        const samePosition = (a, b) => Math.abs(a - b) < 0.01;
        const panelBefore = (item) => secLayout.panels.find((physicalPanel) =>
          samePosition(physicalPanel.startUnits + physicalPanel.widthUnits, item.startUnits));
        const panelAfter = (item) => secLayout.panels.find((physicalPanel) =>
          samePosition(physicalPanel.startUnits, item.startUnits + item.openingUnits));
        const sectionIndexesBeside = (physicalPanel) => ({
          before: secLayout.items.findIndex((item) =>
            samePosition(item.startUnits + item.openingUnits, physicalPanel.startUnits)),
          after: secLayout.items.findIndex((item) =>
            samePosition(item.startUnits, physicalPanel.startUnits + physicalPanel.widthUnits)),
        });

        // `groupStrips` walks the items and yields maximal runs of adjacent
        // members with the same key; each strip spans outer-panel-edge to
        // outer-panel-edge. Two strips meeting at a shared physical panel
        // both cover it at different heights, which is exactly the
        // height-step wrap.
        const drawStrips = (member, keyOf, draw) => {
          let run = null;
          const flush = () => { if (run) { draw(run); run = null; } };
          secLayout.items.forEach((item, i) => {
            if (!member(item, i)) { flush(); return; }
            const key = keyOf(i);
            if (run && run.key === key) run.last = i;
            else { flush(); run = { key, first: i, last: i }; }
          });
          flush();
        };
        const stripSpan = (run) => {
          const lead = panelBefore(secLayout.items[run.first]);
          const trail = panelAfter(secLayout.items[run.last]);
          const startU = lead ? lead.startUnits : secLayout.items[run.first].startUnits;
          const endU = trail ? trail.startUnits + trail.widthUnits
            : secLayout.items[run.last].startUnits + secLayout.items[run.last].openingUnits;
          return { x: toX(panel.x + startU), width: Math.max((endU - startU) * scale, 1) };
        };
        if (opts.crownEnabled === true) {
          const height = opts.crownMouldingHeightUnits > 0 ? opts.crownMouldingHeightUnits : 3;
          drawStrips(
            (item, i) => !item.open && stackHasCrownTrim(unitsOf(i)),
            (i) => Math.max(...unitsOf(i).map((u) => u.topUnits || 0)),
            (run) => {
              const { x, width } = stripSpan(run);
              viewport.append(el('rect', {
                x, y: toY(run.key + height), width, height: Math.max(height * scale, 1),
                class: 'elev-trim elev-trim--crown',
              }));
            },
          );
        }
        if (opts.baseTrimEnabled === true) {
          const height = opts.baseTrimHeightUnits > 0 ? opts.baseTrimHeightUnits : 2.5;
          const shelf = opts.sectionShelfThicknessUnits > 0 ? opts.sectionShelfThicknessUnits : 0.75;
          const ratio = Number.isFinite(opts.baseTrimShelfOverlapRatio)
            ? Math.max(0, opts.baseTrimShelfOverlapRatio) : 1 / 3;
          const shown = height + shelf * ratio;
          drawStrips(
            (item, i) => !item.open && stackHasBaseTrim(secs[i], unitsOf(i)),
            () => 'floor', // the floor does not step — adjacency alone groups
            (run) => {
              const { x, width } = stripSpan(run);
              viewport.append(el('rect', {
                x, y: toY(shown), width, height: Math.max(shown * scale, 1),
                class: 'elev-trim elev-trim--base',
              }));
            },
          );
        }
        // A divider spans the CONTINUOUS carcass beside it: the run section plus
        // every FLUSH unit stacked on it, because a flush unit's side boards are
        // that same panel carried higher (ADR 0041, revised 2026-07-29). Both
        // rules are pure geometry, so they live in geometry/stacking.js and are
        // unit-tested there rather than only being visible on screen.
        const isFlush = (u, item) => isFlushUnit(u, item?.openingUnits ?? 0);
        const extentOf = (i) => carcassExtent(unitsOf(i), secLayout.items[i]?.openingUnits ?? 0);

        secLayout.panels.forEach((p) => {
          // A divider spans the union of the stacks it borders.
          const { before, after } = sectionIndexesBeside(p);
          const a = extentOf(before);
          const b = extentOf(after);
          const tops = [a?.topUnits, b?.topUnits].filter((v) => v > 0);
          if (!tops.length) return;
          const topU = Math.max(...tops);
          const bottoms = [a?.bottomUnits, b?.bottomUnits].filter((v) => Number.isFinite(v));
          const botU = bottoms.length ? Math.min(...bottoms) : 0;
          viewport.append(el('rect', {
            x: toX(panel.x + p.startUnits),
            y: toY(topU),
            width: Math.max(p.widthUnits * scale, 1),
            height: Math.max((topU - botU) * scale, 1),
            class: `elev-section-panel${runOverflows ? ' elev-section-panel--warn' : ''}`,
          }));
        });

        // The ¾" top and bottom shelf every unit carries (owner, 2026-07-28).
        // The bottom one is raised 2½" on a floor-standing unit to clear base
        // trim, and sits at the panel bottom on a platform — the model resolves
        // which, so the drawing just renders what it is told.
        secLayout.items.forEach((item, i) => {
          unitsOf(i).forEach((u, k) => {
            if (!u.shelves) return;
            const spanW = u.widthUnits > 0 ? u.widthUnits : item.openingUnits;
            const spanX = item.startUnits + (u.offsetUnits > 0 ? u.offsetUnits : 0);
            for (const [shelf, kind] of [[u.shelves.cap, 'cap'], [u.shelves.bottom, ''], [u.shelves.top, '']]) {
              if (!shelf) continue;
              viewport.append(el('rect', {
                x: toX(panel.x + spanX),
                y: toY(shelf.toUnits),
                width: Math.max(spanW * scale, 1),
                height: Math.max((shelf.toUnits - shelf.fromUnits) * scale, 1),
                class: `elev-unit-shelf${kind === 'cap' ? ' elev-unit-shelf--cap' : ''}`,
              }));
            }
            void k;
          });
        });

        // A stacked unit's own body + its code caption (ADR 0041). The RUN
        // section's band is drawn by the caption pass below; here we draw
        // everything resting on it, each in its own placed band.
        secLayout.items.forEach((item, i) => {
          const units = unitsOf(i);
          for (let k = 1; k < units.length; k += 1) {
            const u = units[k];
            if (!(u.heightUnits > 0)) continue;
            // Over a base a unit can be narrower and positioned along it; above
            // a non-base it inherits the span below (ADR 0041).
            const spanW = u.widthUnits > 0 ? u.widthUnits : item.openingUnits;
            const spanX = item.startUnits + (u.offsetUnits > 0 ? u.offsetUnits : 0);
            const ux = toX(panel.x + spanX);
            const uw = Math.max(spanW * scale, 1);
            viewport.append(el('rect', {
              x: ux, y: toY(u.topUnits), width: uw, height: Math.max(u.heightUnits * scale, 1),
              class: `elev-stacked-unit${u.overflow ? ' elev-section-panel--warn' : ''}`,
            }));
            // Its OWN side panels, at ITS width — a stacked unit narrower than
            // the base keeps its panels over itself, not over the base below
            // (owner, 2026-07-28). A FLUSH unit draws none: its boards ARE the
            // run's dividers, which rise through it (owner, 2026-07-29) — drawing
            // them here too put a second panel inboard of the base's, leaving the
            // base's ¾" sticking out beside the stacked unit.
            const pt = (opts.panelThicknessUnits > 0 ? opts.panelThicknessUnits : 0.75) * scale;
            if (!isFlush(u, item)) {
              for (const sx of [ux, ux + uw - pt]) {
                viewport.append(el('rect', {
                  x: sx, y: toY(u.topUnits), width: Math.max(pt, 1),
                  height: Math.max(u.heightUnits * scale, 1),
                  class: `elev-section-panel${runOverflows ? ' elev-section-panel--warn' : ''}`,
                }));
              }
            }
            // The stored vertical nudge, the same contract as the run
            // caption below (owner, 2026-08-29: "labels for stacked units
            // don't appear to be moveable" — this caption had neither the
            // nudge read nor a drag handler).
            const stackNudgePx = Number.isFinite(u.section?.elevCodeDyUnits) ? u.section.elevCodeDyUnits * scale : 0;
            const cap = el('text', {
              x: ux + uw / 2, y: toY(u.bottomUnits + u.heightUnits * 0.55) + stackNudgePx,
              'text-anchor': 'middle', class: 'elev-section-code',
              'data-stacked-caption': 'true',
            });
            cap.textContent = codeWithDepth(u.section?.typeCode, u.depthUnits);
            // VERTICAL only: a stacked unit shares its run slot (order =
            // below.order), so a horizontal slide has no reorder to express —
            // do not "complete" this into the run caption's two-axis drag.
            if (u.section) {
              cap.addEventListener('pointerdown', (evt) => beginSectionDrag(evt, cap, {
                id: u.section.id,
                verticalOnly: true,
              }));
            }
            viewport.append(cap);
          }
        });

        // Composed INTERIORS (ADR 0055, plan §5/4b): rods, adjustable/fixed
        // shelves, drawer faces, shoe shelves, and the section's door face —
        // solved by the model per placed unit (`interiorFor` feeds
        // interiorForUnit → solveInterior in the unit's clear band, ADR 0054)
        // and flattened to marks by the pure elevation-interior module, so
        // every position here is real geometry, not decoration. Drawn AFTER
        // the carcass bodies so a stacked unit's fill sits under its own
        // interior, and BEFORE the captions so the code text stays readable
        // on top. Non-interactive, like the rest of the run.
        secLayout.items.forEach((item, i) => {
          unitsOf(i).forEach((u) => {
            const feed = opts.interiorFor?.(u);
            if (!feed) return;
            const spanW = u.widthUnits > 0 ? u.widthUnits : item.openingUnits;
            const spanX = item.startUnits + (u.offsetUnits > 0 ? u.offsetUnits : 0);
            const marks = interiorMarks(feed, spanW, {
              pairCenterRevealUnits: opts.doorPairCenterRevealUnits,
              pairMinWidthUnits: opts.doorPairMinWidthUnits,
              // A rod draws at its REAL diameter (ADR 0062 §8) — a rule.
              hangRodDiameterUnits: opts.hangRodDiameterUnits,
              // The two independent dimension toggles (ADR 0061 item E).
              showShelfSpacing: opts.shelfSpacingDimsVisible === true,
              showDrawerFaceHeights: opts.drawerFaceDimsVisible === true,
              // HANDLE rules (ADR 0064), forwarded under their resolved-rule
              // names so handleRules() reads them directly. Every one of them
              // is admin-editable data, not a constant in the renderer.
              handleTypes: opts.handleTypes,
              defaultHandleType: opts.defaultHandleType,
              defaultHandleLengthMm: opts.defaultHandleLengthMm,
              handleTwoPullMinFaceWidthUnits: opts.handleTwoPullMinFaceWidthUnits,
              handleSinglePullMinWidthFraction: opts.handleSinglePullMinWidthFraction,
              handlePairPositions: opts.handlePairPositions,
              drawerFaceSideOverlayUnits: opts.drawerFaceSideOverlayUnits,
              // The interior editor's row↔picture highlight (ADR 0067). Only
              // the editor preview asks for it; a wall elevation never does.
              showSpaceRegions: opts.showSpaceRegions === true,
            });
            if (!marks.length) return;
            const g = el('g', { class: 'elev-interior' });
            const mx = (units) => toX(panel.x + spanX + units);
            // DRAG TARGETS (owner ruling, 2026-08-29), gated PER UNIT and not
            // per surface. Two reasons, and the second is a correctness one:
            //
            //  - the wall elevation and the measurement sheet must stay
            //    non-interactive (`.elev-interior` is `pointer-events: none`
            //    wholesale) — the sheet is a printed document and the wall's
            //    pointer budget is already spent on pan/pinch/erase;
            //  - the editor's preview draws the WHOLE STACK plus its flanking
            //    panels, but only ONE unit's composition is the open draft. An
            //    element index means something only inside that draft, so a
            //    target on a stack-mate would write the edited unit's shelf
            //    when the designer dragged a different unit's board. The
            //    predicate answers per unit, exactly as `interiorFor` does.
            const draggable = opts.shelfDragTargetsFor?.(u) === true;
            const grabBands = draggable ? shelfGrabBands(marks, scale) : null;
            // WHICH COMPOSITION an element index belongs to. In the editor there
            // is one draft and the index alone is unambiguous; on the WALL
            // elevation the same drawing carries every section on the wall and
            // every stacked unit above them, so index 1 exists many times over
            // and means a different board each time. The section id is what
            // makes it an address rather than an offset.
            const unitSectionId = u.section?.id ?? null;
            if (draggable && unitSectionId) {
              // Where each of those shelves may travel, captured at DRAW time:
              // the pointer handlers run against the picture the designer is
              // actually looking at, not a fresh solve that may have moved on.
              for (const [index, range] of shelfDragBounds(marks, feed.band ?? {})) {
                shelfTravel.set(shelfKey(unitSectionId, index), range);
              }
            }
            for (const m of marks) {
              const warn = m.warn ? ' elev-int--warn' : '';
              if (m.kind === 'spaceRegion') {
                // The region ONE space row is talking about (ADR 0067),
                // carrying that row's element index so the editor can light it
                // up without re-rendering the drawing. Invisible until it is
                // highlighted; never interactive.
                const rect = el('rect', {
                  x: mx(m.fromX), y: toY(m.toUnits),
                  width: Math.max((m.toX - m.fromX) * scale, 1),
                  height: Math.max((m.toUnits - m.fromUnits) * scale, 1),
                  class: 'elev-int-space',
                });
                rect.setAttribute('data-space-index', String(m.elementIndex));
                g.append(rect);
              } else if (m.kind === 'rod') {
                // The rod as a REAL 1 1/4" BODY spanning the whole opening
                // (owner, 2026-08-09 — ADR 0062 §8), hanging from `atUnits`,
                // which is its TOP edge and the datum the two-hole drop lands
                // on. The end drop brackets are gone: they existed to stop a
                // 1px line reading as a shelf, and at full width they would
                // simply redraw the dividers the rod already runs between.
                g.append(el('rect', {
                  x: mx(m.fromX), y: toY(m.atUnits),
                  width: Math.max((m.toX - m.fromX) * scale, 1),
                  // The floor is ONE pixel, not two: two would distort the true
                  // proportion the owner asked for at small scales, and one is
                  // all it takes to stay visible (ADR 0062 §8).
                  height: Math.max(m.diameterUnits * scale, 1),
                  class: `elev-int-rod${warn}`,
                }));
              } else if (m.kind === 'hanger') {
                // A hanger hanging from the rod (ADR 0060): the hook loops up
                // over the rod line, the shoulders hang below it — so a rod
                // cannot be read as a shelf. Pure decoration of a REAL rod
                // position, which is why it is a mark and not an icon.
                const hx = mx(m.x);
                const hy = toY(m.atUnits);
                g.append(el('line', {
                  x1: hx, y1: toY(m.atUnits + m.hookRiseUnits), x2: hx, y2: hy,
                  class: `elev-int-hanger${warn}`,
                }));
                const shoulderY = toY(m.atUnits - m.dropUnits);
                g.append(el('polyline', {
                  points: [
                    `${hx.toFixed(1)},${hy.toFixed(1)}`,
                    `${mx(m.x - m.halfWidthUnits).toFixed(1)},${shoulderY.toFixed(1)}`,
                    `${mx(m.x + m.halfWidthUnits).toFixed(1)},${shoulderY.toFixed(1)}`,
                    `${hx.toFixed(1)},${hy.toFixed(1)}`,
                  ].join(' '),
                  class: `elev-int-hanger${warn}`,
                }));
              } else if (m.kind === 'shelf') {
                // EVERY shelf is a BOARD at its real thickness (owner,
                // 2026-08-09 — ADR 0062 §8): ¾", or 1½" when tagged
                // double-thick, from the one `shelfThicknessUnits()` source the
                // solver already resolved. Fixed vs adjustable is told apart by
                // FILL (solid vs outline) now that both have real height —
                // thickness can no longer carry that distinction because
                // thickness is the true dimension.
                g.append(el('rect', {
                  x: mx(m.fromX), y: toY(m.atUnits),
                  width: Math.max((m.toX - m.fromX) * scale, 1),
                  height: Math.max(m.thicknessUnits * scale, 1),
                  class: `elev-int-shelf${m.doubleThick ? ' elev-int-shelf-thick' : ''}${m.fixed ? ' elev-int-shelf--fixed' : ''}${warn}`,
                }));
                // The GRAB target: invisible, taller than the board, and the
                // only thing in this group that takes a pointer. It carries the
                // element index draftSetShelfPin() writes to — the same index
                // the editor's "Pin at" field already edits, so the drag is an
                // accelerator over a path that works without it (WCAG 2.2 SC
                // 2.5.7). A group member has no index and gets no rect.
                // A padlock on a LOCKED board (owner, 2026-08-30), sized in
                // screen px so it stays legible at any zoom — the same glyph
                // and the same reasoning as the locked fixture's. The state
                // belongs on the DRAWING, not only in a list: the designer
                // finds out why a board will not drag by looking at it.
                if (m.locked === true) {
                  const pad = el('text', {
                    x: mx((m.fromX + m.toX) / 2), y: toY(m.atUnits) - 3,
                    'text-anchor': 'middle', class: 'elev-int-shelf__lock',
                  });
                  pad.textContent = '🔒';
                  g.append(pad);
                }
                const band = unitSectionId ? grabBands?.get(m.elementIndex) : null;
                if (band) {
                  const grab = el('rect', {
                    x: mx(m.fromX), y: toY(band.toUnits),
                    width: Math.max((m.toX - m.fromX) * scale, 1),
                    height: Math.max((band.toUnits - band.fromUnits) * scale, 1),
                    class: 'elev-int-shelf-grab',
                  });
                  grab.setAttribute('data-shelf-index', String(m.elementIndex));
                  grab.setAttribute('data-shelf-section', unitSectionId);
                  g.append(grab);
                }
              } else if (m.kind === 'spacingDim' || m.kind === 'faceDim') {
                // A dimension figure between two ticks (ADR 0061 item E). The
                // PURE module decided it is worth drawing in UNITS; here the
                // final say is in PIXELS, because legibility is a screen fact:
                // at tablet zoom a real 4" gap can still be 6px tall.
                const h = m.spanUnits * scale;
                if (h >= 13) {
                  const dx = mx(m.x);
                  const y1 = toY(m.fromUnits);
                  const y2 = toY(m.toUnits);
                  const cls = m.kind === 'faceDim' ? 'elev-int-dim--face' : 'elev-int-dim--gap';
                  const dim = el('g', { class: `elev-int-dim ${cls}` });
                  dim.append(el('line', { x1: dx, y1, x2: dx, y2 }));
                  for (const ty of [y1, y2]) {
                    dim.append(el('line', { x1: dx - 3, y1: ty, x2: dx + 3, y2: ty }));
                  }
                  const label = el('text', {
                    x: dx + 3, y: (y1 + y2) / 2 + 3, class: 'elev-int-dim__label',
                  });
                  // ¼"-rounded imperial (ADR 0061, the owner's display rule).
                  const fmtDim = opts.formatSystemLength ?? opts.formatLength;
                  label.textContent = fmtDim ? fmtDim(m.spanUnits) : `${Math.round(m.spanUnits)}"`;
                  dim.append(label);
                  g.append(dim);
                }
              } else if (m.kind === 'drawer') {
                // The face rectangle at its REAL solved height and REAL width —
                // proud of the opening by the ¼" side overlay (ADR 0064) — plus
                // its handles, one or two, placed by the shared handle rule.
                g.append(el('rect', {
                  x: mx(m.fromX), y: toY(m.toUnits),
                  width: Math.max((m.toX - m.fromX) * scale, 1),
                  height: Math.max((m.toUnits - m.fromUnits) * scale, 1),
                  class: `elev-int-drawer${warn}`,
                }));
                for (const h of m.handles ?? []) {
                  const py = toY(h.atUnits);
                  if (h.draw === 'knob') {
                    // A knob is a POINT: it has no meaningful length to span,
                    // so it draws as the dot a door pull does.
                    g.append(el('circle', { cx: mx(h.x), cy: py, r: 2.5, class: 'elev-int-pull-knob' }));
                  } else {
                    g.append(el('line', {
                      x1: mx(h.fromX), y1: py, x2: mx(h.toX), y2: py, class: 'elev-int-pull',
                    }));
                  }
                }
              } else if (m.kind === 'shoeShelf') {
                // One thin shelf per solved pitch. riseUnits > 0 tilts the
                // line — the SSS slant SYMBOL (a front view cannot show the
                // real tilt); FSS stays flat at rise 0.
                g.append(el('line', {
                  x1: mx(m.fromX), y1: toY(m.atUnits + m.riseUnits), x2: mx(m.toX), y2: toY(m.atUnits),
                  class: `elev-int-shoe${warn}`,
                }));
              } else if (m.kind === 'door') {
                // OUTLINE-ONLY over its coverage band, pull dot at the latch
                // side — over the interior, which stays readable through it
                // (ADR 0055).
                g.append(el('rect', {
                  x: mx(m.fromX), y: toY(m.toUnits),
                  width: Math.max((m.toX - m.fromX) * scale, 1),
                  height: Math.max((m.toUnits - m.fromUnits) * scale, 1),
                  class: `elev-int-door${m.warn ? ' elev-int--warn' : ''}`,
                }));
                if (m.pull.draw === 'bar') {
                  const half = (m.pull.lengthUnits > 0 ? m.pull.lengthUnits : 3.75) / 2;
                  g.append(el('line', {
                    x1: mx(m.pull.x), y1: toY(m.pull.atUnits + half),
                    x2: mx(m.pull.x), y2: toY(m.pull.atUnits - half),
                    class: 'elev-int-door-pull elev-int-door-pull--bar',
                  }));
                } else {
                  g.append(el('circle', { cx: mx(m.pull.x), cy: toY(m.pull.atUnits), r: 2.5, class: 'elev-int-door-pull' }));
                }
              } else if (m.kind === 'doorSplit') {
                // The edge upper and lower doors meet at (ADR 0060 item 3).
                const y = toY(m.atUnits);
                g.append(el('line', {
                  x1: mx(m.fromX), y1: y, x2: mx(m.toX), y2: y,
                  class: `elev-int-door-split${m.warn ? ' elev-int--warn' : ''}`,
                }));
              }
            }
            viewport.append(g);
          });
        });

        // Platform legs (ADR 0040): each raised section that stands on legs
        // shows its two FRONT legs (of 4) from the floor up to the section
        // bottom. Drawn per opening, inset from its edges.
        if (platformUnits > 0) {
          secLayout.items.forEach((item, i) => {
            const place = unitsOf(i)[0]; // only the stack's BASE stands on legs
            if (!place || !place.hasLegs || !(place.bottomUnits > 0)) return;
            const legTop = toY(place.bottomUnits);
            const legH = place.bottomUnits * scale;
            const legW = Math.max(Math.min(1.5 * scale, item.openingUnits * scale * 0.12), 2);
            const inset = Math.min(item.openingUnits * 0.12, 3) * scale;
            const leftX = toX(panel.x + item.startUnits) + inset;
            const rightX = toX(panel.x + item.startUnits + item.openingUnits) - inset - legW;
            for (const lx of [leftX, rightX]) {
              viewport.append(el('rect', {
                x: lx, y: legTop, width: legW, height: legH, class: 'elev-platform-leg',
              }));
            }
          });
        }
        secLayout.items.forEach((item, i) => {
          const cxOpen = toX(panel.x + item.startUnits + item.openingUnits / 2);
          // Hanging-into-hanging is a PLAN collision warning. It should not
          // turn an otherwise valid front-elevation caption red.
          const warn = item.narrow || item.wide || runOverflows;
          // Caption follows the section's platform placement: an open section
          // has no body, so it floats at a nominal height raised by the platform.
          const nominalH = opts.sectionDefaultHeightUnits > 0 ? opts.sectionDefaultHeightUnits : 84;
          const place = unitsOf(i)[0] ?? { bottomUnits: platformUnits, topUnits: platformUnits + nominalH };
          const bandU = place.topUnits - place.bottomUnits;
          // The stored vertical nudge (owner, 2026-08-28): room units through
          // the same scale as everything else, + = down, so a pulled-apart
          // caption pair stays apart at every zoom and across reloads.
          const nudgePx = Number.isFinite(secs[i]?.elevCodeDyUnits) ? secs[i].elevCodeDyUnits * scale : 0;
          const code = el('text', {
            x: cxOpen, y: toY(place.bottomUnits + bandU * 0.62) + nudgePx,
            'text-anchor': 'middle',
            class: `elev-section-code${warn ? ' elev-section--warn' : ''}${item.open ? ' elev-section-code--open' : ''}`,
          });
          code.textContent = codeWithDepth(secs[i]?.typeCode, place.depthUnits);
          // Drag the code sideways to reorder the section on this wall (owner,
          // 2026-07-26). Horizontal only: order is the only free variable —
          // the solver owns the actual positions.
          const sec = secs[i];
          if (sec) {
            code.addEventListener('pointerdown', (evt) => beginSectionDrag(evt, code, {
              id: sec.id,
              wallId: panel.id,
              panelX: panel.x,
              panelW: panel.width,
              others: secLayout.items.filter((_, j) => j !== i).map((o) => o.startUnits + o.openingUnits / 2),
            }));
          }
          // BELOW THE FLOOR LINE (owner, 2026-08-09: "The width of the section
          // should be shown below the floor, not at the floor, to avoid it
          // being blocked"). At the floor it sat inside the carcass, where a
          // base section's body or a drawer face covered it. The wall name and
          // length rows move down by the same drop, so the below-floor stack
          // reads: section widths, wall name, wall length.
          const width = el('text', {
            x: cxOpen, y: floorY + widthLabelDrop,
            'text-anchor': 'middle',
            class: `elev-section-width${warn ? ' elev-section--warn' : ''}`,
          });
          width.textContent = (opts.formatLength ? opts.formatLength(item.openingUnits) : `${Math.round(item.openingUnits)}"`)
            + (item.narrow ? ' ⚠ under min' : item.wide ? ' ⚠ over max' : '');
          viewport.append(code, width);
        });
        // Hang-clearance zones (ADR 0038): the run's reserved end(s), kept
        // clear for the neighbour wall's hanging clothes. Drawn as a hatched
        // band with a caption so the empty space reads as intentional.
        // An L-shelf leg reserves the same way but is BUILT, so it is drawn
        // instead of hatched; only the part of the reservation no leg fills
        // stays hatched (ADR 0042).
        for (const [clear, atStart] of [[secLayout.startClearanceUnits, true], [secLayout.endClearanceUnits, false]]) {
          if (!(clear > 0)) continue;
          const filled = Math.min(legCoverage(atStart), clear);
          if (!(clear - filled > 0.01)) continue;
          const zx = atStart ? toX(panel.x + filled) : toX(panel.x + panel.width - clear);
          const zw = (clear - filled) * scale;
          // The reserved zone rides the platform with its corner (hanging) section.
          const cornerPlace = unitsOf(atStart ? 0 : secs.length - 1)[0]
            ?? { bottomUnits: platformUnits, topUnits: Math.max(secH(atStart ? 0 : secs.length - 1), 1) + platformUnits };
          const zTop = cornerPlace.topUnits;
          const zBot = cornerPlace.bottomUnits;
          viewport.append(el('rect', {
            x: zx, y: toY(zTop), width: zw, height: Math.max((zTop - zBot) * scale, 1),
            class: 'elev-hang-clearance',
          }));
          const cap = el('text', {
            x: zx + zw / 2, y: toY(zBot + (zTop - zBot) * 0.35), 'text-anchor': 'middle',
            class: 'elev-section-width',
          });
          const remain = clear - filled;
          cap.textContent = `${opts.formatLength ? opts.formatLength(remain) : remain} hang`;
          viewport.append(cap);
        }
        if (secLayout.overflowUnits > 0.01) {
          const over = el('text', {
            x: x + w - 4, y: toY(0) - 22, 'text-anchor': 'end',
            class: 'elev-section-width elev-section--warn',
          });
          over.textContent = `▲ over by ${opts.formatLength ? opts.formatLength(secLayout.overflowUnits) : secLayout.overflowUnits}`;
          viewport.append(over);
        }
      }

      // The L-shelf legs themselves. The shelf's body is in the OTHER wall's
      // run, so nothing above draws them — but the leg is a real board on this
      // wall and the designer builds from this elevation. Vertical placement
      // comes from the model (the shelf's own placement in its host run), so
      // the two elevations can never show the same shelf at two heights.
      for (const leg of lLegs) {
        const place = leg.place;
        if (!place || !(place.topUnits > place.bottomUnits)) continue;
        const lx = toX(panel.x + leg.fromUnits);
        const lw = Math.max((leg.toUnits - leg.fromUnits) * scale, 1);
        const lTop = toY(place.topUnits);
        const lh = Math.max((place.topUnits - place.bottomUnits) * scale, 1);
        viewport.append(el('rect', { x: lx, y: lTop, width: lw, height: lh, class: 'elev-lshelf-leg' }));
        // ONE side panel, at the far end (owner, 2026-08-28): an L-shelf has
        // exactly two end panels — host far side and leg far end — and the
        // corner side is continuous, closed by the ¾" backer. The panel board
        // comes precomputed from the model so plan and elevation draw the
        // identical geometry.
        if (leg.endPanel) {
          const px = toX(panel.x + leg.endPanel.fromUnits);
          const pw = Math.max((leg.endPanel.toUnits - leg.endPanel.fromUnits) * scale, 1);
          viewport.append(el('rect', {
            x: px, y: lTop, width: pw, height: lh, class: 'elev-section-panel',
          }));
        }
        for (const [shelf, kind] of [[place.shelves?.cap, 'cap'], [place.shelves?.bottom, ''], [place.shelves?.top, '']]) {
          if (!shelf) continue;
          viewport.append(el('rect', {
            x: lx, y: toY(shelf.toUnits), width: lw,
            height: Math.max((shelf.toUnits - shelf.fromUnits) * scale, 1),
            class: `elev-unit-shelf${kind === 'cap' ? ' elev-unit-shelf--cap' : ''}`,
          }));
        }
        // The interior shelf boards WRAP the corner (owner, 2026-08-28): one
        // physical board per shelf, continued across the leg at the HOST
        // interior's solved heights — handed over precomputed, same rule as
        // `place`, so the two elevations can never disagree.
        for (const board of leg.shelfBoards ?? []) {
          viewport.append(el('rect', {
            x: lx, y: toY(board.atUnits), width: lw,
            height: Math.max(board.thicknessUnits * scale, 1),
            class: 'elev-int-shelf',
          }));
        }
        // On a platform the leg stands on the same two front legs as any other
        // raised unit (ADR 0040).
        if (place.hasLegs && place.bottomUnits > 0) {
          const legSpanU = leg.toUnits - leg.fromUnits;
          const legW = Math.max(Math.min(1.5 * scale, legSpanU * scale * 0.12), 2);
          const inset = Math.min(legSpanU * 0.12, 3) * scale;
          for (const fx of [lx + inset, lx + lw - inset - legW]) {
            viewport.append(el('rect', {
              x: fx, y: toY(place.bottomUnits), width: legW,
              height: place.bottomUnits * scale, class: 'elev-platform-leg',
            }));
          }
        }
        const lcx = lx + lw / 2;
        const band = place.topUnits - place.bottomUnits;
        const lcode = el('text', {
          x: lcx, y: toY(place.bottomUnits + band * 0.62), 'text-anchor': 'middle',
          class: 'elev-section-code',
        });
        // The leg's own depth — the two legs of an L-shelf size independently.
        lcode.textContent = sectionCodeLabel(leg.typeCode ?? 'L', leg.depthUnits, opts.formatLength);
        // Below the floor with the section widths, for the same reason.
        const llen = el('text', {
          x: lcx, y: floorY + widthLabelDrop, 'text-anchor': 'middle',
          class: 'elev-section-width',
        });
        const legLen = leg.toUnits - leg.fromUnits;
        llen.textContent = opts.formatLength ? opts.formatLength(legLen) : `${Math.round(legLen)}"`;
        viewport.append(lcode, llen);
      }

      // Label below the floor: "W3" and the length — under the section width
      // captions, which now claim the first row below the floor (ADR 0060).
      const cx = x + w / 2;
      const name = el('text', { x: cx, y: floorY + widthLabelDrop + 18, 'text-anchor': 'middle', class: `elev-label__name${selected ? ' is-selected' : ''}` });
      name.textContent = `W${panel.wallNumber ?? '?'}`;
      const len = el('text', { x: cx, y: floorY + widthLabelDrop + 34, 'text-anchor': 'middle', class: 'elev-label__len' });
      len.textContent = opts.formatLength ? opts.formatLength(panel.lengthUnits) : `${Math.round(panel.lengthUnits)}"`;
      viewport.append(name, len);
    }

    // Freehand annotations LAST, so they sit on top of the elevation. Each is
    // anchored to a wall panel and its points are in UNITS relative to that
    // panel (x along the wall from the panel's left, y up from floor). We
    // convert to viewport pixels with the CURRENT layout, so a stroke moves and
    // rescales with its panel when other walls are hidden/shown (ADR 0026).
    // `annotationStrokes` keeps the viewport-space geometry for the eraser.
    annotationStrokes = [];
    const panelById = new Map(layout.panels.map((p) => [p.id, p]));
    for (const a of Array.isArray(opts.annotations) ? opts.annotations : []) {
      let pts;
      if (a.wallId) {
        const panel = panelById.get(a.wallId);
        if (!panel) continue; // anchor wall hidden → its sketch/note hides with it
        pts = a.points.map((q) => ({ x: toX(panel.x + q.x), y: toY(q.y) }));
      } else {
        // Legacy stroke (pre-anchoring): points are raw viewport pixels.
        pts = a.points;
      }
      if (a.kind === 'text') {
        const p = pts[0];
        if (!p) continue;
        const t = el('text', { x: p.x, y: p.y, class: 'annotation-text', fill: a.color, 'data-annotation-id': a.id });
        // Inches × THIS drawing's scale, and inline rather than an attribute —
        // see the matching note in canvas.js for both reasons.
        if (a.fontSize > 0) t.style.fontSize = `${(a.fontSize * scale).toFixed(2)}px`;
        t.textContent = a.text ?? '';
        t.addEventListener('pointerdown', (evt) => beginTextDrag(evt, t));
        viewport.append(t);
        annotationStrokes.push({ id: a.id, width: a.width, points: pts, kind: 'text' });
        continue;
      }
      annotationStrokes.push({ id: a.id, width: a.width, points: pts });
      viewport.append(el('polyline', {
        points: pts.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' '),
        class: 'annotation',
        stroke: a.color,
        'stroke-width': a.width,
        'data-annotation-id': a.id,
      }));
    }

    // Standard section-type labels (ADR 0034): on the panel at (offsetAlongWall
    // from the panel's left, offsetFromFloor up). Horizontal/upright — the
    // elevation is already a developed front view.
    for (const label of Array.isArray(opts.wallLabels) ? opts.wallLabels : []) {
      const panel = panelById.get(label.wallId);
      if (!panel) continue; // its wall isn't in the elevation
      const x = toX(panel.x + (label.offsetAlongWall ?? 0));
      const y = toY(label.offsetFromFloor ?? 0);
      const t = el('text', { x, y, class: 'wall-label-note', 'text-anchor': 'middle', 'data-wall-label-id': label.id });
      t.textContent = label.text;
      t.addEventListener('pointerdown', (evt) => beginLabelDrag(evt, t));
      t.addEventListener('contextmenu', (evt) => { evt.preventDefault(); handlers.onLabelMenu?.(label.id, evt.clientX, evt.clientY); });
      viewport.append(t);
    }

    fitViewBoxToDrawing(opts.fitViewBoxToContent === true);
  }

  /**
   * Shrink the viewBox onto what was actually drawn — for the SECTION EDITOR's
   * preview only (owner, 2026-08-30: "the section should be scaled to fill the
   * preview panel, right now it is only about 40% of the vertical height").
   *
   * ## Why it was 40%, which is the whole reason this exists
   *
   * The drawing was fit TWICE, to two different shapes, and the second fit
   * threw away the first:
   *
   *   1. render() scales content into the fixed 1200x560 viewBox — a WALL's
   *      aspect, 2.14:1. One tall narrow unit is height-bound there, so it fills
   *      that box's height but barely an eighth of its width.
   *   2. `preserveAspectRatio="xMidYMid meet"` then fits that whole wide box
   *      into the preview panel, which is roughly SQUARE. Now width binds:
   *      min(521/1200, 547/560) = 0.434, and the drawing shrinks by another
   *      57% — most of it spent on empty viewBox either side of the unit.
   *
   * Measured on the owner's panel: 40% of the available height. With the box
   * trimmed to the drawing, 97%.
   *
   * The wall elevation is genuinely wall-shaped, so it never noticed; the
   * measurement sheet must keep its fixed box because a printed page is a fixed
   * page. Hence the opt — only the preview asks.
   *
   * Zoom and pan are a transform on `.elev-viewport`, and getBBox() reports a
   * group's bounds in its OWN user space, before that transform — so the box
   * stays put while the designer zooms rather than chasing them.
   *
   * getBBox is guarded: it needs a laid-out SVG document, which the node tests'
   * node shim deliberately is not. Without it the fixed box stands, which is
   * exactly the old behaviour.
   */
  function fitViewBoxToDrawing(on) {
    if (!on) {
      elevSvg.setAttribute('viewBox', `0 0 ${ELEV_W} ${ELEV_H}`);
      return;
    }
    const box = typeof viewport.getBBox === 'function' ? viewport.getBBox() : null;
    if (!box || !(box.width > 0) || !(box.height > 0)) return;
    // A little air, so strokes on the outermost edges are not clipped by the
    // very box that was measured from them.
    const pad = VIEWBOX_FIT_PAD;
    elevSvg.setAttribute(
      'viewBox',
      `${box.x - pad} ${box.y - pad} ${box.width + pad * 2} ${box.height + pad * 2}`,
    );
  }

  // ---- annotation gestures (ADR 0023) --------------------------------------

  /**
   * Converts a freshly-drawn stroke (viewport pixels) to a wall-anchored one:
   * points in UNITS relative to the panel nearest the stroke's start (ADR 0026).
   * A note drawn OUTSIDE all panels anchors to the nearest one with an offset
   * (negative x / large x / negative y), so it still travels with that panel.
   * Returns { wallId, points } or null if there is nothing to anchor to.
   */
  function anchorStroke(vpPoints) {
    if (!frame || vpPoints.length < 2 || !frame.layout.panels.length) return null;
    const toU = (p) => ({ u: (p.x - frame.originX) / frame.scale, v: (frame.floorY - p.y) / frame.scale });
    const stripPts = vpPoints.map(toU);
    const first = stripPts[0];
    let anchor = frame.layout.panels.find((p) => first.u >= p.x && first.u <= p.x + p.width);
    if (!anchor) {
      anchor = frame.layout.panels.reduce(
        (best, p) => {
          const d = first.u < p.x ? p.x - first.u : first.u > p.x + p.width ? first.u - (p.x + p.width) : 0;
          return d < best.d ? { p, d } : best;
        },
        { p: frame.layout.panels[0], d: Infinity },
      ).p;
    }
    return { wallId: anchor.id, points: stripPts.map((s) => ({ x: s.u - anchor.x, y: s.v })) };
  }

  /** Anchor a single viewport point to the nearest panel, in panel-relative units. */
  function anchorPoint(vpPoint) {
    if (!frame || !frame.layout.panels.length) return null;
    const u = (vpPoint.x - frame.originX) / frame.scale;
    const v = (frame.floorY - vpPoint.y) / frame.scale;
    let anchor = frame.layout.panels.find((p) => u >= p.x && u <= p.x + p.width);
    if (!anchor) {
      anchor = frame.layout.panels.reduce(
        (best, p) => {
          const d = u < p.x ? p.x - u : u > p.x + p.width ? u - (p.x + p.width) : 0;
          return d < best.d ? { p, d } : best;
        },
        { p: frame.layout.panels[0], d: Infinity },
      ).p;
    }
    return { wallId: anchor.id, point: { x: u - anchor.x, y: v } };
  }

  /** Text tool on the elevation: float an input, anchor the note to a panel. */
  function promptTextAt(clientX, clientY) {
    const anchored = anchorPoint(toViewportPoint(clientX, clientY));
    if (!anchored) return;
    floatTextInput(clientX, clientY, annotationStyle.color, (value) => {
      if (value.trim()) handlers.onAddText?.(anchored.point, value, { ...annotationStyle }, anchored.wallId);
    });
  }

  /**
   * Start dragging a text note to reposition it — only in view mode (in
   * annotate/erase/text the tap falls through to those tools). On release the
   * note re-anchors to whatever panel it landed on (ADR 0033).
   */
  function beginTextDrag(evt, node) {
    if (mode !== 'view') return;
    evt.stopPropagation();
    textDrag = {
      id: node.getAttribute('data-annotation-id'),
      node,
      origX: parseFloat(node.getAttribute('x')) || 0,
      origY: parseFloat(node.getAttribute('y')) || 0,
      grab: toViewportPoint(evt.clientX, evt.clientY),
      last: null,
    };
    elevSvg.setPointerCapture(evt.pointerId);
  }

  /** Drag a wall label on the panel; on release it re-anchors (offset + height). */
  function beginLabelDrag(evt, node) {
    if (mode !== 'view') return;
    evt.stopPropagation();
    labelDrag = {
      id: node.getAttribute('data-wall-label-id'),
      node,
      origX: parseFloat(node.getAttribute('x')) || 0,
      origY: parseFloat(node.getAttribute('y')) || 0,
      grab: toViewportPoint(evt.clientX, evt.clientY),
      last: null,
    };
    elevSvg.setPointerCapture(evt.pointerId);
  }

  /** Drag a section code sideways to reorder it on its wall (view mode only). */
  function beginSectionDrag(evt, node, info) {
    if (mode !== 'view') return;
    evt.stopPropagation();
    sectionDrag = {
      ...info,
      node,
      origX: parseFloat(node.getAttribute('x')) || 0,
      origY: parseFloat(node.getAttribute('y')) || 0,
      grab: toViewportPoint(evt.clientX, evt.clientY),
      slot: null,
      dyPx: 0,
    };
    elevSvg.setPointerCapture(evt.pointerId);
  }

  function distToSegment(p, a, b) {
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const len2 = dx * dx + dy * dy;
    if (len2 === 0) return Math.hypot(p.x - a.x, p.y - a.y);
    let t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / len2;
    t = Math.max(0, Math.min(1, t));
    return Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy));
  }

  function eraseAt(clientX, clientY) {
    const p = toViewportPoint(clientX, clientY);
    const tol = 8 / view.scale;
    for (let i = annotationStrokes.length - 1; i >= 0; i--) {
      const a = annotationStrokes[i];
      // A text note: hit-test its rendered box (its anchor alone would miss).
      if (a.kind === 'text') {
        const node = viewport.querySelector(`[data-annotation-id="${a.id}"]`);
        const box = node?.getBoundingClientRect();
        if (box && clientX >= box.left - 6 && clientX <= box.right + 6 && clientY >= box.top - 6 && clientY <= box.bottom + 6) {
          handlers.onEraseAnnotation?.(a.id);
          return;
        }
        continue;
      }
      let min = Infinity;
      for (let j = 1; j < a.points.length; j++) min = Math.min(min, distToSegment(p, a.points[j - 1], a.points[j]));
      if (min <= tol + a.width / 2) {
        handlers.onEraseAnnotation?.(a.id);
        return;
      }
    }
  }

  /**
   * Draw the mini plan with the active wall highlighted.
   * @param {object[]} walls plan chain
   * @param {string|null} selectedId
   */
  function renderThumbnail(walls, selectedId, partitionFaces = []) {
    thumbSvg.replaceChildren();
    const pts = walls.flatMap((w) => [w.start, w.end]);
    if (pts.length === 0) return;

    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const p of pts) {
      if (p.x < minX) minX = p.x;
      if (p.x > maxX) maxX = p.x;
      if (p.y < minY) minY = p.y;
      if (p.y > maxY) maxY = p.y;
    }
    const w = Math.max(maxX - minX, 1);
    const h = Math.max(maxY - minY, 1);
    const scale = Math.min((THUMB_W - THUMB_PAD * 2) / w, (THUMB_H - THUMB_PAD * 2) / h);
    const ox = (THUMB_W - w * scale) / 2 - minX * scale;
    const oy = (THUMB_H - h * scale) / 2 - minY * scale;
    const tx = (p) => ox + p.x * scale;
    const ty = (p) => oy + p.y * scale;

    for (const seg of walls) {
      const isOpening = (seg.segmentKind ?? 'wall') === 'opening';
      const selected = seg.id === selectedId;
      // Grey the walls excluded from the elevation, matching the plan view, so
      // the mini plan shows which walls the elevation is currently displaying.
      const elevHidden = !isOpening && seg.includeInElevation === false;
      thumbSvg.append(el('line', {
        x1: tx(seg.start), y1: ty(seg.start), x2: tx(seg.end), y2: ty(seg.end),
        class: `thumb-wall${isOpening ? ' thumb-wall--opening' : ''}${selected ? ' thumb-wall--selected' : ''}${elevHidden ? ' thumb-wall--elev-hidden' : ''}`,
        'data-id': seg.id,
      }));
    }

    // Interior/partition walls (ADR 0032): the boundary above doesn't include the
    // spur poking into the room, so draw each partition's peninsula outline —
    // its two faces plus the tip cap — from the derived face-walls, whose
    // start=footprint edge and end=tip.
    const facesBySeg = new Map();
    for (const f of partitionFaces) {
      const list = facesBySeg.get(f.partitionSegmentId) ?? [];
      list.push(f);
      facesBySeg.set(f.partitionSegmentId, list);
    }
    for (const [, pair] of facesBySeg) {
      if (pair.length < 2) continue;
      const a = pair.find((f) => f.side === 'A') ?? pair[0];
      const b = pair.find((f) => f.side === 'B') ?? pair[1];
      const sel = a.id === selectedId || b.id === selectedId || a.partitionSegmentId === selectedId;
      // baseL → tipL → tipR → baseR (the footprint edge closes the U on the wall).
      const outline = [a.start, a.end, b.end, b.start];
      thumbSvg.append(el('polyline', {
        points: outline.map((p) => `${tx(p).toFixed(1)},${ty(p).toFixed(1)}`).join(' '),
        class: `thumb-wall thumb-partition${sel ? ' thumb-wall--selected' : ''}`,
        fill: 'none',
      }));
    }
  }

  // ---- pan / zoom / tap-select --------------------------------------------
  //
  // Drag pans, wheel and two-finger pinch zoom, and a tap (a pointer that did
  // not drag) selects the panel under it. Distinguishing a tap from a drag is
  // what lets the same surface both pan and select.

  const TAP_SLOP = 4; // px of movement below which a gesture counts as a tap
  const activePointers = new Map();
  let panStart = null; // { last, downTarget, moved }
  let pinch = null;
  let itemDrag = null; // { id, node, start, grabDx, grabDy, moved, ... }
  let textDrag = null; // repositioning a text note: { id, node, origX, origY, grab, last }
  let labelDrag = null; // repositioning a wall label on the panel: same shape
  let sectionDrag = null; // sliding a section code sideways to REORDER it (ADR 0037)
  let shelfDrag = null; // dragging one shelf up/down its travel: { index, grabDy, last }

  // Annotation (design-sketch) state, mirroring the plan canvas (ADR 0023).
  let mode = 'view'; // 'view' | 'annotate' | 'erase'
  let annotationStyle = { color: '#e5484d', width: 2 };
  let annotationStraight = false;
  let annotationStrokes = []; // last-rendered strokes, for eraser hit-testing
  let annotDrawing = null; // { points, node, straight, anchor }
  const setElevMode = (next) => { mode = next; };
  const setElevAnnotationStyle = (style) => { annotationStyle = { ...annotationStyle, ...style }; };
  const setElevAnnotationStraight = (on) => { annotationStraight = Boolean(on); };

  /**
   * Starts dragging ONE shelf up or down (owner rulings, 2026-08-29).
   *
   * Vertical only: `atUnits` is the single number a shelf carries, so sideways
   * movement has nothing to write to and is ignored rather than resisted.
   *
   * The grab offset is recorded for the same reason the fixture drag records
   * one — a board that jumps its own top edge under the finger on the first
   * move feels like it was snatched. It keeps the grip the designer took.
   *
   * Returns false when the shelf has no recorded travel, which lets the caller
   * fall through to ordinary pan/tap handling instead of swallowing the
   * gesture on a board that cannot move.
   */
  function beginShelfDrag(sectionId, index, clientX, clientY) {
    const range = shelfTravel.get(shelfKey(sectionId, index));
    const u = toUnits(clientX, clientY);
    if (!range || !u) return false;
    const from = clampShelfDrag(range, u.y);
    if (from === null) return false;
    // ANCHOR THE OTHERS FIRST. A shelf without a pin solves in flow, so pinning
    // this one would re-cut the segments and visibly move boards the designer
    // never touched — ruling 3 says a drag moves ONE shelf. The handler
    // materializes the solved positions as pins before anything moves; the
    // picture is unchanged by it, so the range captured above still holds.
    handlers.onShelfDragStart?.(index, sectionId);
    shelfDrag = { sectionId, index, range, grabDy: u.y - from, moved: false };
    return true;
  }

  /**
   * Writes the shelf's new top elevation on every move, not on release.
   *
   * Live, because the WRITE is what snaps: `draftSetShelfPin` feeds the solver,
   * the solver lands the pin on a real system hole, and the re-render draws it
   * there. The designer sees the board step hole to hole under their finger
   * using the app's own snapper — there is no preview ghost to keep in step
   * with the model, because the model IS the preview.
   *
   * Ruling 4 (no confirmation, no undo) is why this needs no commit on release:
   * every move already wrote to the draft, and the editor's existing Apply /
   * Cancel are still the only things that decide whether it persists.
   */
  function moveShelfDrag(clientX, clientY) {
    if (!shelfDrag) return;
    const u = toUnits(clientX, clientY);
    if (!u) return;
    const next = clampShelfDrag(shelfDrag.range, u.y - shelfDrag.grabDy);
    if (next === null) return;
    shelfDrag.moved = true;
    handlers.onDragShelf?.(shelfDrag.index, next, shelfDrag.sectionId);
  }

  /** Ends the gesture. Nothing to commit — every move already wrote. */
  function endShelfDrag() {
    shelfDrag = null;
  }

  /**
   * Starts dragging an already-placed fixture. The grab offset is recorded so
   * the item does not jump its own centre under the finger on the first move —
   * it keeps whatever grip the user took hold of.
   */
  function beginItemDrag(id, node, clientX, clientY) {
    const drawn = drawnItems.get(id);
    const u = toUnits(clientX, clientY);
    if (!drawn || !u) return false;
    // A locked fixture never enters a drag: returning false here lets the
    // caller fall through to normal pan/tap handling, so the surface still
    // pans under a locked item and a tap on it still selects its wall.
    if (drawn.locked) return false;
    itemDrag = {
      id,
      node,
      drawn,
      grabDx: u.x - drawn.stripX,
      grabDy: u.y - drawn.offsetFromFloor,
      stripX: drawn.stripX,
      offsetFromFloor: drawn.offsetFromFloor,
      moved: false,
    };
    node.classList.add('elev-obstruction--dragging');
    return true;
  }

  function moveItemDrag(clientX, clientY) {
    const u = toUnits(clientX, clientY);
    if (!u || !frame) return;
    itemDrag.stripX = u.x - itemDrag.grabDx;
    itemDrag.offsetFromFloor = u.y - itemDrag.grabDy;
    const dx = (itemDrag.stripX - itemDrag.drawn.stripX) * frame.scale;
    // Screen y is inverted relative to height-above-floor.
    const dy = -(itemDrag.offsetFromFloor - itemDrag.drawn.offsetFromFloor) * frame.scale;
    if (Math.abs(dx) + Math.abs(dy) > TAP_SLOP) itemDrag.moved = true;
    itemDrag.node.setAttribute('transform', `translate(${dx} ${dy})`);
  }

  function endItemDrag() {
    const drag = itemDrag;
    itemDrag = null;
    drag.node.classList.remove('elev-obstruction--dragging');
    if (!drag.moved) {
      // A tap on a fixture selects the wall it sits on, so the measurement box
      // opens on the right wall without a separate trip to the plan.
      handlers.onSelectWall?.(drag.drawn.wallId);
      return;
    }
    // Which wall it landed on is decided by the item's centre, not its corner.
    const centre = drag.stripX + drag.drawn.widthUnits / 2;
    const panel = panelAtStripX(centre);
    const wallId = panel ? panel.id : drag.drawn.wallId;
    const panelX = panel ? panel.x : frame.layout.panels.find((p) => p.id === wallId)?.x ?? 0;
    handlers.onMoveObstruction?.(drag.id, {
      wallId,
      offsetAlongWall: drag.stripX - panelX,
      offsetFromFloor: drag.offsetFromFloor,
    });
  }

  function pinchMetrics() {
    const [a, b] = [...activePointers.values()];
    return { dist: Math.hypot(a.x - b.x, a.y - b.y), mid: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 } };
  }

  elevSvg.addEventListener('pointerdown', (evt) => {
    activePointers.set(evt.pointerId, { x: evt.clientX, y: evt.clientY });
    if (activePointers.size === 2) {
      panStart = null;
      // A second finger means the designer wants to ZOOM, not to keep dragging.
      // The board stays wherever the last move already wrote it — ruling 4 has
      // no undo, so abandoning the gesture must not roll anything back either.
      endShelfDrag();
      const { dist, mid } = pinchMetrics();
      pinch = { startDist: dist, startMid: mid, startView: { ...view } };
      return;
    }
    if (activePointers.size > 2) return;

    // Sketch/erase take over the surface when active, so a pen drag draws
    // instead of panning.
    if (mode === 'annotate') {
      const p = toViewportPoint(evt.clientX, evt.clientY);
      const node = el('polyline', { points: `${p.x},${p.y}`, class: 'annotation', stroke: annotationStyle.color, 'stroke-width': annotationStyle.width });
      viewport.append(node);
      annotDrawing = { points: [p], node, straight: annotationStraight, anchor: p };
      elevSvg.setPointerCapture(evt.pointerId);
      return;
    }
    if (mode === 'erase') {
      eraseAt(evt.clientX, evt.clientY);
      panStart = { last: toSvgPoint(evt.clientX, evt.clientY), downTarget: evt.target, moved: false, erasing: true };
      elevSvg.setPointerCapture(evt.pointerId);
      return;
    }
    if (mode === 'text') {
      promptTextAt(evt.clientX, evt.clientY);
      return;
    }

    // A pointer that lands on a SHELF drags that shelf. Ahead of the fixture
    // check only because the two never coexist — a shelf target is emitted on
    // the section editor's preview and a fixture on the wall elevation — so the
    // order states intent rather than resolving a real contest. Behind the mode
    // tools above, because annotate/erase/text deliberately take the whole
    // surface when they are on.
    const shelfHit = evt.target?.closest?.('[data-shelf-index]');
    if (shelfHit) {
      const index = Number(shelfHit.getAttribute('data-shelf-index'));
      const sectionId = shelfHit.getAttribute('data-shelf-section');
      if (Number.isInteger(index) && sectionId
        && beginShelfDrag(sectionId, index, evt.clientX, evt.clientY)) {
        elevSvg.setPointerCapture(evt.pointerId);
        return;
      }
    }

    // A pointer that lands on a fixture drags THAT, not the whole view — this
    // is the only way to move something on a touch screen, where there is no
    // modifier key to distinguish "pan" from "grab".
    const hit = evt.target?.closest?.('[data-obstruction-id]');
    if (hit && beginItemDrag(hit.getAttribute('data-obstruction-id'), hit, evt.clientX, evt.clientY)) {
      elevSvg.setPointerCapture(evt.pointerId);
      return;
    }

    panStart = { last: toSvgPoint(evt.clientX, evt.clientY), downTarget: evt.target, moved: false };
    elevSvg.setPointerCapture(evt.pointerId);
  });

  elevSvg.addEventListener('pointermove', (evt) => {
    if (activePointers.has(evt.pointerId)) activePointers.set(evt.pointerId, { x: evt.clientX, y: evt.clientY });

    if (shelfDrag) {
      moveShelfDrag(evt.clientX, evt.clientY);
      return;
    }

    if (sectionDrag && frame) {
      const p = toViewportPoint(evt.clientX, evt.clientY);
      if (!sectionDrag.verticalOnly) {
        // Horizontal slide, clamped to the section's own wall panel.
        const minX = frame.originX + sectionDrag.panelX * frame.scale;
        const maxX = minX + sectionDrag.panelW * frame.scale;
        const nx = Math.max(minX, Math.min(maxX, sectionDrag.origX + (p.x - sectionDrag.grab.x)));
        sectionDrag.node.setAttribute('x', nx);
        const along = (nx - minX) / frame.scale; // units from the panel's left
        sectionDrag.slot = sectionDrag.others.filter((mid) => mid < along).length;
      }
      // The vertical component nudges the LABEL, not the section (owner,
      // 2026-08-28): captions colliding at one height can be pulled apart.
      const ny = sectionDrag.origY + (p.y - sectionDrag.grab.y);
      sectionDrag.node.setAttribute('y', ny);
      sectionDrag.dyPx = ny - sectionDrag.origY;
      return;
    }

    if (textDrag || labelDrag) {
      const drag = textDrag ?? labelDrag;
      const p = toViewportPoint(evt.clientX, evt.clientY);
      const nx = drag.origX + (p.x - drag.grab.x);
      const ny = drag.origY + (p.y - drag.grab.y);
      drag.node.setAttribute('x', nx);
      drag.node.setAttribute('y', ny);
      drag.last = { x: nx, y: ny };
      return;
    }

    if (annotDrawing) {
      const p = toViewportPoint(evt.clientX, evt.clientY);
      if (annotDrawing.straight) {
        annotDrawing.points = [annotDrawing.anchor, p];
        annotDrawing.node.setAttribute('points', annotDrawing.points.map((q) => `${q.x.toFixed(1)},${q.y.toFixed(1)}`).join(' '));
        return;
      }
      const last = annotDrawing.points[annotDrawing.points.length - 1];
      if (Math.hypot(p.x - last.x, p.y - last.y) > 1.5 / view.scale) {
        annotDrawing.points.push(p);
        annotDrawing.node.setAttribute('points', annotDrawing.points.map((q) => `${q.x.toFixed(1)},${q.y.toFixed(1)}`).join(' '));
      }
      return;
    }
    if (panStart?.erasing) {
      eraseAt(evt.clientX, evt.clientY);
      return;
    }

    if (itemDrag) {
      moveItemDrag(evt.clientX, evt.clientY);
      return;
    }

    if (pinch && activePointers.size === 2) {
      const { dist, mid } = pinchMetrics();
      const newScale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, pinch.startView.scale * (dist / (pinch.startDist || 1))));
      const startMid = toSvgPoint(pinch.startMid.x, pinch.startMid.y);
      const curMid = toSvgPoint(mid.x, mid.y);
      const anchorX = (startMid.x - pinch.startView.x) / pinch.startView.scale;
      const anchorY = (startMid.y - pinch.startView.y) / pinch.startView.scale;
      view = { scale: newScale, x: curMid.x - anchorX * newScale, y: curMid.y - anchorY * newScale };
      applyView();
      return;
    }

    if (panStart) {
      const cur = toSvgPoint(evt.clientX, evt.clientY);
      const dx = cur.x - panStart.last.x;
      const dy = cur.y - panStart.last.y;
      if (Math.hypot(cur.x - panStart.last.x, cur.y - panStart.last.y) > 0) {
        if (Math.abs(dx) + Math.abs(dy) > TAP_SLOP) panStart.moved = true;
        view = panBy(view, dx, dy);
        panStart.last = cur;
        applyView();
      }
    }
  });

  function endPointer(evt) {
    activePointers.delete(evt.pointerId);
    if (activePointers.size < 2) pinch = null;
    if (shelfDrag) {
      // Nothing to commit: every move already wrote through the setter, and a
      // drag that never moved wrote nothing, so a tap on a shelf is a no-op
      // rather than a re-pin at the position it already had.
      endShelfDrag();
      try { elevSvg.releasePointerCapture(evt.pointerId); } catch { /* not captured */ }
      return;
    }
    if (sectionDrag) {
      if (sectionDrag.slot !== null) handlers.onSectionReorder?.(sectionDrag.id, sectionDrag.wallId, sectionDrag.slot);
      // A few px of jitter is a tap, not a nudge; anything more is stored in
      // room units so the placement is zoom-independent.
      if (frame && Math.abs(sectionDrag.dyPx) > 2) {
        handlers.onSectionLabelNudge?.(sectionDrag.id, sectionDrag.dyPx / frame.scale);
      }
      sectionDrag = null;
      try { elevSvg.releasePointerCapture(evt.pointerId); } catch { /* not captured */ }
      return;
    }
    if (textDrag) {
      const pos = textDrag.last ?? { x: textDrag.origX, y: textDrag.origY };
      const anchored = anchorPoint(pos);
      if (anchored) handlers.onMoveText?.(textDrag.id, anchored.point, anchored.wallId);
      textDrag = null;
      try { elevSvg.releasePointerCapture(evt.pointerId); } catch { /* not captured */ }
      return;
    }
    if (labelDrag) {
      const pos = labelDrag.last ?? { x: labelDrag.origX, y: labelDrag.origY };
      const anchored = anchorPoint(pos);
      // Panel-relative point → along-wall offset + height above floor.
      if (anchored) handlers.onMoveLabel?.(labelDrag.id, { wallId: anchored.wallId, offsetAlongWall: anchored.point.x, offsetFromFloor: anchored.point.y });
      labelDrag = null;
      try { elevSvg.releasePointerCapture(evt.pointerId); } catch { /* not captured */ }
      return;
    }
    if (annotDrawing) {
      const { points, node } = annotDrawing;
      annotDrawing = null;
      node.remove(); // the committed stroke re-renders from the model
      const anchored = anchorStroke(points);
      if (anchored) handlers.onAnnotate?.(anchored.points, { ...annotationStyle }, anchored.wallId);
      try { elevSvg.releasePointerCapture(evt.pointerId); } catch { /* not captured */ }
      return;
    }
    if (panStart?.erasing) {
      panStart = null;
      try { elevSvg.releasePointerCapture(evt.pointerId); } catch { /* not captured */ }
      return;
    }
    if (itemDrag) {
      endItemDrag();
      try { elevSvg.releasePointerCapture(evt.pointerId); } catch { /* not captured */ }
      return;
    }
    if (panStart && !panStart.moved) {
      // A tap: select the panel under the pointer, if any.
      const target = panStart.downTarget?.closest?.('[data-id]');
      handlers.onSelectWall?.(target ? target.getAttribute('data-id') : null);
    }
    panStart = null;
    try { elevSvg.releasePointerCapture(evt.pointerId); } catch { /* not captured */ }
  }
  elevSvg.addEventListener('pointerup', endPointer);
  elevSvg.addEventListener('pointercancel', endPointer);

  elevSvg.addEventListener('wheel', (evt) => {
    evt.preventDefault();
    const p = toSvgPoint(evt.clientX, evt.clientY);
    view = zoomAt(view, view.scale * (evt.deltaY < 0 ? 1.12 : 1 / 1.12), p.x, p.y);
    applyView();
  }, { passive: false });

  function zoomBy(factor) {
    view = zoomAt(view, view.scale * factor, ELEV_W / 2, ELEV_H / 2);
    applyView();
  }

  return {
    render,
    renderThumbnail,
    zoomIn: () => zoomBy(1.2),
    zoomOut: () => zoomBy(1 / 1.2),
    fit: () => { view = identityView(); applyView(); }, // content is drawn fit-to-view already
    // Used by the palette, which drags from outside this SVG and so has to ask
    // whether a given screen point is over the elevation, and what it means.
    containsPoint,
    placementAt,
    setMode: setElevMode,
    setAnnotationStyle: setElevAnnotationStyle,
    setAnnotationStraight: setElevAnnotationStraight,
  };
}
